/**
 * Workspace Component
 *
 * The main editor interface for Core Carousel. This is the largest component in the app,
 * handling all slide editing, image generation, and export functionality.
 *
 * LAYOUT: Three-panel design
 * - Left sidebar: Slide list with thumbnails, batch mode controls
 * - Center canvas: Live slide preview with zoom controls
 * - Right sidebar: Slide properties, image settings, global options
 *
 * KEY FEATURES:
 * - Individual and batch image generation
 * - Image upload with AI stylization
 * - PNG export (single slide or ZIP of all)
 * - Markdown text editing with toolbar
 * - Theme, color, and layout customization
 */

import React, { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from '@/contexts/LanguageContext';
import { Slide, Profile, CarouselStyle, CarouselProject, SlideType, AspectRatio, Theme, FontStyle, ContentLayout, LayoutSettings, TextAlignment, Language, AnalisePreCriacao, ChecklistValidacao, SlideNumberPosition } from '../types';
import TwitterSlide from './TwitterSlide';
import StorytellerSlide from './StorytellerSlide';

import RichTextEditor from './RichTextEditor';
import { trackFunnelEvent } from '@/services/adminDashboardService';
import { logger } from '@/lib/logger';
import { generateSlideImage, stylizeImage, editImage, refineCarouselContent, getApiAspectRatio, IMAGE_MODEL_PRO, IMAGE_MODEL_FLASH, DEFAULT_IMAGE_STYLE } from '../services/geminiService';
import { apiPost, ApiError } from '@/services/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn, generateId } from '@/lib/utils';
import { compressThumbnail, parseDataUrl, inlineExternalImages, restoreOriginalImages, blobToDataUrl, extractBase64FromDataUrl, downscaleImageDataUrl, prefetchAllImageUrls, inlineImagesFromCache } from '@/lib/imageCompression';
import { getFontEmbedCSS, collectUsedFontFaces } from '@/lib/fontEmbed';
import { EXPORT_WIDTH, getExportHeight } from '../lib/exportDimensions';
import { buildFallbackImagePrompt } from '../lib/imagePromptFallback';
import { hasBlankImageRegions } from '@/lib/captureVerify';
import {
  Plus, Trash2, Download, ArrowLeft, Zap, Upload, X, Loader2,
  ChevronDown, ChevronUp, Settings, Image, Type, Palette,
  CheckSquare, Square, RefreshCw, Sparkles, Pencil, Sun, Moon,
  Save, FolderOpen, GripVertical, Copy, LayoutGrid, RotateCcw
} from 'lucide-react';
import { useOnboarding } from '@/contexts/OnboardingContext';
import MobileToolbar, { MobileToolbarPanel } from '@/components/mobile/MobileToolbar';
import MobileEditor from '@/components/mobile/MobileEditor';

type EditorTheme = 'light' | 'dark';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const EDIT_IMAGE_MAX_BASE64_LENGTH = 3_500_000;
const EDIT_IMAGE_INITIAL_MAX_DIMENSION = 1920;
const EDIT_IMAGE_MIN_DIMENSION = 768;
const EDIT_IMAGE_INITIAL_QUALITY = 0.88;
const EDIT_IMAGE_MIN_QUALITY = 0.58;
const EDIT_IMAGE_MAX_COMPRESSION_ATTEMPTS = 4;

class LocalEditImageError extends Error {
  code: 'EDIT_IMAGE_TOO_LARGE' | 'EDIT_IMAGE_PROCESS_FAILED';

  constructor(message: string, code: 'EDIT_IMAGE_TOO_LARGE' | 'EDIT_IMAGE_PROCESS_FAILED') {
    super(message);
    this.name = 'LocalEditImageError';
    this.code = code;
  }
}

/**
 * Hook to detect mobile viewport (< 768px).
 * Uses matchMedia for efficient, real-time detection.
 */
function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
}

/**
 * Detects iOS WebKit (iPhone, iPad, iPod).
 * All iOS browsers use WebKit under the hood, even Chrome.
 * iPadOS 13+ reports "MacIntel" as platform, so we also check maxTouchPoints.
 */
function isIOSWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * Downloads a Blob with iOS-specific handling.
 * iOS Safari doesn't support the `download` attribute reliably for images,
 * so we open the blob in a new tab where the user can long-press to save.
 * ZIP files work fine with saveAs on iOS, so only images get special treatment.
 */
function downloadBlobForPlatform(blob: Blob, filename: string) {
  if (isIOSWebKit() && blob.type.startsWith('image/')) {
    const url = URL.createObjectURL(blob);
    const newTab = window.open(url, '_blank');
    if (!newTab) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      try { document.body.removeChild(link); } catch { /* link may already be removed */ }
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } else {
    window.saveAs(blob, filename);
  }
}

interface WorkspaceProps {
  slides: Slide[];
  profile: Profile;
  style: CarouselStyle;
  aspectRatio: AspectRatio;
  onUpdateSlides: (slides: Slide[]) => void;
  onStyleChange?: (style: CarouselStyle) => void;  // For style conversion
  onBack: () => void;
  projectName: string;
  saveStatus: SaveStatus;
  editorTheme?: EditorTheme;
  onEditorThemeToggle?: () => void;
  language?: Language;
  // Project ID for Storage uploads
  projectId?: string;
  // Conteúdo Notável metadata (from AI generation)
  analisePreCriacao?: AnalisePreCriacao;
  checklistValidacao?: ChecklistValidacao;
  // Thumbnail callback - called with base64 or URL when first slide changes
  onThumbnailGenerated?: (thumbnailUrl: string) => void;
  // When true (e.g. ?autoExport=1 from the chat flow), automatically trigger
  // batch carousel download once on first successful mount.
  autoExportOnMount?: boolean;
}

// External libraries loaded via CDN in index.html
declare global {
  interface Window {
    htmlToImage: any;  // PNG export library (SVG foreignObject approach)
    html2canvas: any;  // PNG export fallback for iOS (Canvas 2D approach)
    JSZip: any;        // ZIP creation for batch export
    saveAs: any;       // File download helper
  }
}

// ============================================================================
// SORTABLE SLIDE ITEM - Drag-and-drop slide card for sidebar
// ============================================================================
interface SortableSlideItemProps {
  slide: Slide;
  index: number;
  isActive: boolean;
  batchMode: boolean;
  isSelected: boolean;
  generationStatus?: 'idle' | 'generating' | 'success' | 'error';
  isGenerating: boolean;
  onSelect: () => void;
  onToggleSelection: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

const SortableSlideItem: React.FC<SortableSlideItemProps> = ({
  slide,
  index,
  isActive,
  batchMode,
  isSelected,
  generationStatus,
  isGenerating,
  onSelect,
  onToggleSelection,
  onDelete,
  onDuplicate,
}) => {
  const { t } = useTranslation();
  const [showDragHandle, setShowDragHandle] = useState(false);
  const getSlideTypeLabel = (type: SlideType) => {
    if (type === SlideType.COVER) return t('workspace.slideTypeCover');
    if (type === SlideType.CTA) return t('workspace.slideTypeImage');
    return t('workspace.slideTypeContent');
  };
  const getSlidePreviewText = (content: string) => {
    const cleaned = content
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/(\*\*|__|\*|_|~~|`)/g, '')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      .replace(/\s+/g, ' ')
      .trim();
    return cleaned || '...';
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id, disabled: batchMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => !batchMode && onSelect()}
      className={cn(
        "relative p-3 rounded-xl cursor-pointer transition-all border group",
        isActive && !batchMode
          ? "bg-primary/15 border-primary/70 text-foreground"
          : "bg-card border-border hover:border-primary/40 hover:bg-secondary/40",
        batchMode && "cursor-default"
      )}
    >
      <div className="flex justify-between items-center gap-2">
        <div className="flex items-center min-w-0 flex-1">
          {/* Checkbox for batch mode */}
          {batchMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelection}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            />
          )}

          <div
            className="relative h-8 flex items-center min-w-0 flex-1"
            onMouseLeave={() => setShowDragHandle(false)}
          >
            {!batchMode && (
              <>
                <div
                  className="absolute -left-1 top-0 h-8 w-8 z-20"
                  onPointerEnter={() => setShowDragHandle(true)}
                />
                <div
                  {...attributes}
                  {...listeners}
                  className={cn(
                    "absolute left-0 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-md cursor-grab active:cursor-grabbing transition-all duration-200 z-30",
                    showDragHandle ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-1 pointer-events-none",
                    isActive ? "text-primary hover:text-primary" : "text-muted-foreground hover:text-foreground"
                  )}
                  onPointerEnter={() => setShowDragHandle(true)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GripVertical className="h-4 w-4" />
                </div>
              </>
            )}

            <div
              className={cn(
                "flex items-center gap-3 min-w-0 transition-transform duration-200",
                !batchMode && showDragHandle ? "translate-x-6" : "translate-x-0"
              )}
            >
              <div
                className={cn(
                  "w-7 h-7 aspect-square rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
                  isActive && !batchMode
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground"
                )}
              >
                {index + 1}
              </div>

              <div className="min-w-0 h-8 flex items-center">
                <p className={cn(
                  "text-xs font-semibold uppercase tracking-wide",
                  isActive && !batchMode ? "text-primary" : "text-muted-foreground"
                )}>
                  {getSlideTypeLabel(slide.type)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Generation Status Indicator (batch mode) */}
          {generationStatus && (
            <span className={`text-xs ${generationStatus === 'generating' ? 'text-yellow-400 animate-pulse' :
              generationStatus === 'success' ? 'text-green-400' :
                generationStatus === 'error' ? 'text-red-400' : ''
              }`}>
              {generationStatus === 'generating' && '...'}
              {generationStatus === 'success' && '✓'}
              {generationStatus === 'error' && '✗'}
            </span>
          )}

          {/* Individual generation indicator (non-batch mode) */}
          {!generationStatus && isGenerating && (
            <span className="text-xs text-primary animate-pulse">...</span>
          )}

          {!batchMode && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate();
                }}
                className="h-6 w-6 text-muted-foreground hover:text-foreground md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                title={t('workspace.duplicateSlide')}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                title={t('workspace.deleteSlide')}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>
      <p className="mt-2 text-sm opacity-95 leading-snug truncate">
        {getSlidePreviewText(slide.content)}
      </p>
    </div>
  );
};

const Workspace: React.FC<WorkspaceProps> = ({ slides, profile, style, aspectRatio, onUpdateSlides, onStyleChange, onBack, projectName, saveStatus, editorTheme = 'light', onEditorThemeToggle, language = 'pt-BR', projectId, analisePreCriacao, checklistValidacao, onThumbnailGenerated, autoExportOnMount = false }) => {
  const { t } = useTranslation();
  const { restartTour } = useOnboarding();
  const DEFAULT_SLIDE_IMAGE_MODEL = IMAGE_MODEL_PRO;
  const DEFAULT_GENERATE_IMAGE_MODEL = IMAGE_MODEL_FLASH;
  const DEFAULT_SLIDE_IMAGE_RATIO: AspectRatio = '16/9';
  // ============================================================================
  // MOBILE STATE
  // ============================================================================
  const isMobile = useIsMobile();
  const [mobilePanel, setMobilePanel] = useState<MobileToolbarPanel | null>(null);

  // ============================================================================
  // CORE STATE
  // ============================================================================
  const [activeSlideId, setActiveSlideId] = useState<string>(slides[0]?.id ?? '');
  const [generatingSlideIds, setGeneratingSlideIds] = useState<Set<string>>(new Set()); // Per-slide loading state
  const [isDownloading, setIsDownloading] = useState(false);
  const [showExportPopover, setShowExportPopover] = useState(false);
  const [exportMode, setExportMode] = useState<'all' | 'selected'>('all');
  const [selectedExportSlideIds, setSelectedExportSlideIds] = useState<Set<string>>(
    new Set(slides[0]?.id ? [slides[0].id] : [])
  );
  const exportPopoverRef = useRef<HTMLDivElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // PATTERN: Stale Closure Workaround
  // Problem: Async handlers (image generation, stylization) capture `slides` at call time.
  // If user edits other slides while waiting, those changes would be lost when we update.
  // Solution: Keep a ref that always points to the CURRENT slides array.
  // Usage: In async handlers, use slidesRef.current instead of slides from closure.
  const slidesRef = useRef(slides);
  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);

  useEffect(() => {
    setSelectedExportSlideIds((prev) => {
      const validIds = new Set(slides.map((slide) => slide.id));
      const filtered = new Set([...prev].filter((id) => validIds.has(id)));
      if (filtered.size === 0 && activeSlideId && validIds.has(activeSlideId)) {
        filtered.add(activeSlideId);
      }
      return filtered;
    });
  }, [slides, activeSlideId]);

  useEffect(() => {
    if (!showExportPopover) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowExportPopover(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (exportPopoverRef.current?.contains(target) || exportButtonRef.current?.contains(target)) {
        return;
      }
      setShowExportPopover(false);
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportPopover]);

  // ============================================================================
  // GLOBAL SETTINGS (apply to all slides)
  // ============================================================================
  const [showSlideNumbers, setShowSlideNumbers] = useState(true);
  const [slideNumberPosition, setSlideNumberPosition] = useState<SlideNumberPosition>('center');
  const [showVerifiedBadge, setShowVerifiedBadge] = useState(true);
  const [zoomFactor, setZoomFactor] = useState(1);           // 1.0 = fit-to-screen (100%)
  const [fitScale, setFitScale] = useState(1);               // Dynamic scale to fit viewport
  const [pan, setPan] = useState({ x: 0, y: 0 });            // Canvas pan offset in px
  const [headerScale, setHeaderScale] = useState(1.0);       // Profile header size multiplier (0.5 - 2.0)
  const [accentColor, setAccentColor] = useState('#949494'); // Highlight color for markdown
  const [showAccent, setShowAccent] = useState(true);

  // ============================================================================
  // FONT SETTINGS (global, can be overridden per-slide)
  // ============================================================================
  const [fontStyle, setFontStyle] = useState<FontStyle>('MODERN');
  const [fontScale, setFontScale] = useState(1.0); // 0.5 - 2.0 (50%-200%)

  // ============================================================================
  // LAYOUT SETTINGS (global, can be overridden per-slide)
  // ============================================================================
  const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>({
    contentPadding: 64,      // 32-96 px
    imageZoom: 1.0,          // 0.5-2.0 (50%-200%)
    imageMargin: 0,          // 0-32 px
    textLineHeight: 1.5,     // 1.2-2.0
    paragraphGap: 1,         // 0.5-2.0 rem
    textAlignment: 'left',   // left, center, right
  });

  // ============================================================================
  // GLOBAL IMAGE STYLE
  // ============================================================================
  const [globalImageStyle, setGlobalImageStyle] = useState<string>(DEFAULT_IMAGE_STYLE);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // ============================================================================
  // IMAGE GENERATION SETTINGS (fixed defaults)
  // ============================================================================

  // Default theme: Storyteller uses dark (cinematic), Twitter uses light (clean)
  const [theme, setTheme] = useState<Theme>(
    style === CarouselStyle.STORYTELLER ? 'DARK' : 'LIGHT'
  );

  // ============================================================================
  // IMAGE UPLOAD & STYLIZATION STATE
  // ============================================================================
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingUploadImage, setPendingUploadImage] = useState<{
    base64: string;       // Full data URI for preview
    rawBase64: string;    // Raw base64 without prefix (for API)
    mimeType: string;     // e.g., "image/png", "image/jpeg"
    width: number;
    height: number;
    detectedRatio: string;  // Closest API-supported ratio (preserves original proportions)
  } | null>(null);
  const [stylizePrompt, setStylizePrompt] = useState('');
  const [isStylizing, setIsStylizing] = useState(false);

  // ============================================================================
  // AI IMAGE EDITING STATE
  // ============================================================================
  const [showEditImageModal, setShowEditImageModal] = useState(false);
  const [editImagePrompt, setEditImagePrompt] = useState('');

  // ============================================================================
  // BACKGROUND IMAGE GENERATION STATE
  // ============================================================================
  const [generatingBackgroundIds, setGeneratingBackgroundIds] = useState<Set<string>>(new Set()); // Per-slide background generation
  const [backgroundPrompt, setBackgroundPrompt] = useState(''); // User-provided prompt for background generation
  const [isEditingImage, setIsEditingImage] = useState(false);

  // ============================================================================
  // AI CONTENT REFINEMENT STATE
  // ============================================================================
  const [globalFeedback, setGlobalFeedback] = useState('');
  const [slideFeedback, setSlideFeedback] = useState('');
  const [isRefining, setIsRefining] = useState(false);

  /**
   * Finds the closest API-supported aspect ratio for an uploaded image.
   * Used to preserve the original image proportions when stylizing.
   *
   * Algorithm: Linear distance minimization against known API ratios.
   *
   * @param ratio - The actual width/height ratio of the uploaded image
   * @returns API ratio string (e.g., "1:1", "9:16")
   */
  const getClosestApiRatio = (ratio: number): string => {
    const apiRatios = [
      { name: '1:1', value: 1 },
      { name: '4:5', value: 0.8 },
      { name: '9:16', value: 0.5625 },
      { name: '16:9', value: 1.778 },
      { name: '3:4', value: 0.75 },
      { name: '4:3', value: 1.333 },
      { name: '2:3', value: 0.667 },
      { name: '3:2', value: 1.5 },
    ];
    let closest = apiRatios[0];
    let minDiff = Math.abs(ratio - closest.value);
    for (const r of apiRatios) {
      const diff = Math.abs(ratio - r.value);
      if (diff < minDiff) {
        minDiff = diff;
        closest = r;
      }
    }
    return closest.name;
  };

  // ============================================================================
  // BATCH GENERATION STATE
  // These 4 state variables work together to enable multi-slide image generation:
  // - batchMode: UI toggle for checkbox selection mode
  // - selectedSlideIds: Which slides are checked for batch generation
  // - batchGenerating: Global "in progress" flag
  // - slideGenerationStatus: Per-slide status for progress indicators
  // ============================================================================
  const [batchMode, setBatchMode] = useState(false);
  const [selectedSlideIds, setSelectedSlideIds] = useState<Set<string>>(new Set());
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchLoadingStep, setBatchLoadingStep] = useState(0);
  const [slideGenerationStatus, setSlideGenerationStatus] = useState<Record<string, 'idle' | 'generating' | 'success' | 'error'>>({});

  useEffect(() => {
    if (!batchGenerating) {
      setBatchLoadingStep(0);
      return;
    }
    const intervalId = window.setInterval(() => {
      setBatchLoadingStep((prev) => (prev + 1) % 4);
    }, 400);
    return () => window.clearInterval(intervalId);
  }, [batchGenerating]);

  // ============================================================================
  // DRAG-AND-DROP SLIDE REORDERING
  // ============================================================================
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }, // Require 5px drag before activating (prevents accidental drags)
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = slides.findIndex(s => s.id === active.id);
      const newIndex = slides.findIndex(s => s.id === over.id);
      onUpdateSlides(arrayMove(slides, oldIndex, newIndex));
    }
  };

  // ============================================================================
  // SIDEBAR VIEW MODE (toggle between Global and Per-Slide settings)
  // ============================================================================
  const [sidebarView, setSidebarView] = useState<'global' | 'slide'>('slide');

  // Collapsible sections in slide sidebar
  const [imageSettingsOpen, setImageSettingsOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const withImageCacheBust = (url: string): string => {
    if (!url || url.startsWith('data:')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${Date.now()}`;
  };

  // Reset modal state when switching slides (fixes the "can't interact with other slides" bug)
  useEffect(() => {
    setShowUploadModal(false);
    setPendingUploadImage(null);
    setStylizePrompt('');
  }, [activeSlideId]);

  const activeIndex = slides.findIndex(s => s.id === activeSlideId);
  const activeSlide = activeIndex >= 0 ? slides[activeIndex] : slides[0];

  // Reset activeSlideId if it became stale (e.g., slide was deleted externally)
  useEffect(() => {
    if (activeIndex === -1 && slides.length > 0) {
      setActiveSlideId(slides[0].id);
    }
  }, [activeIndex, slides]);

  const handleTextChange = useCallback((text: string) => {
    // Validate activeIndex before updating
    if (activeIndex === -1 || !activeSlide) {
      return;
    }

    const newSlides = [...slides];
    newSlides[activeIndex] = { ...activeSlide, content: text };
    onUpdateSlides(newSlides);
  }, [activeIndex, activeSlide, slides, onUpdateSlides]);

  const handleToggleImage = (checked: boolean) => {
    const newSlides = [...slides];
    const isStoryteller = style === CarouselStyle.STORYTELLER;

    newSlides[activeIndex] = {
      ...activeSlide,
      showImage: checked,
      imageScale: checked ? (activeSlide.imageScale || 45) : undefined, // Default to 45 for Storyteller
      overlayImage: isStoryteller ? true : undefined
    };
    onUpdateSlides(newSlides);
  };

  const handleToggleImageRef = useRef(handleToggleImage);
  handleToggleImageRef.current = handleToggleImage;

  const handleToggleOverlay = (checked: boolean) => {
    const newSlides = [...slides];
    newSlides[activeIndex] = { ...activeSlide, overlayImage: checked };
    onUpdateSlides(newSlides);
  }

  const handleImageScaleChange = (scale: number) => {
    const newSlides = [...slides];
    newSlides[activeIndex] = { ...activeSlide, imageScale: scale };
    onUpdateSlides(newSlides);
  }

  const handleImageOffsetChange = (offset: number) => {
    const newSlides = [...slides];
    newSlides[activeIndex] = { ...activeSlide, imageOffsetY: offset };
    onUpdateSlides(newSlides);
  }

  const handleGradientHeightChange = (height: number) => {
    const newSlides = [...slides];
    newSlides[activeIndex] = { ...activeSlide, gradientHeight: height };
    onUpdateSlides(newSlides);
  }

  const handleDeleteSlide = (slideId: string) => {
    if (slides.length <= 1) return;
    const newSlides = slides.filter(s => s.id !== slideId);
    onUpdateSlides(newSlides);
    // If we deleted the active slide, select the first remaining slide
    if (activeSlideId === slideId) {
      setActiveSlideId(newSlides[0].id);
    }
  };

  const handleAddSlide = () => {
    const isStoryteller = style === CarouselStyle.STORYTELLER;
    const newSlide: Slide = {
      id: generateId(),
      type: SlideType.CONTENT,
      content: "New slide content...",
      showImage: false,
      imageScale: isStoryteller ? 45 : 50,
      overlayImage: isStoryteller ? true : undefined
    };
    const newSlides = [...slides];
    newSlides.splice(activeIndex + 1, 0, newSlide);
    onUpdateSlides(newSlides);
    setActiveSlideId(newSlide.id);
  };

  const handleDuplicateSlide = (slideId: string) => {
    const slideIndex = slides.findIndex(s => s.id === slideId);
    if (slideIndex === -1) return;

    const slideToDuplicate = slides[slideIndex];
    const duplicatedSlide: Slide = {
      ...slideToDuplicate,
      id: generateId(), // New unique ID
    };

    const newSlides = [...slides];
    newSlides.splice(slideIndex + 1, 0, duplicatedSlide); // Insert after the original
    onUpdateSlides(newSlides);
    setActiveSlideId(duplicatedSlide.id); // Select the new slide
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const dataUri = event.target.result as string;
          // Extract mime type and raw base64
          const mimeMatch = dataUri.match(/^data:(image\/\w+);base64,/);
          const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
          const rawBase64 = dataUri.replace(/^data:image\/\w+;base64,/, '');

          // Create an Image to detect dimensions (use window.Image because Image from lucide-react shadows it)
          const img = new window.Image();
          img.onload = () => {
            const width = img.width;
            const height = img.height;
            const ratio = width / height;
            const detectedRatio = getClosestApiRatio(ratio);

            setPendingUploadImage({
              base64: dataUri,
              rawBase64: rawBase64,
              mimeType: mimeType,
              width,
              height,
              detectedRatio
            });
            setShowUploadModal(true);
          };
          img.onerror = () => {
            logger.error('Failed to load image');
            alert(t('workspace.errors.loadImageFailed', { name: file.name }));
          };
          img.src = dataUri;
        }
      };
      reader.onerror = () => {
        logger.error('Failed to read file');
        alert(t('workspace.errors.readFileFailed', { name: file.name }));
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so same file can be selected again
    if (e.target) e.target.value = '';
  };

  const handleUseImageAsIs = async () => {
    if (!pendingUploadImage || !activeSlide) return;

    // Capture slide ID at call time
    const slideId = activeSlide.id;

    let imageUrl = pendingUploadImage.base64;

    // Upload to Storage if projectId is available
    if (projectId) {
      try {
        const response = await apiPost<{ url: string }>('/api/images/upload', {
          projectId,
          slideId,
          imageType: 'slide',
          base64: pendingUploadImage.rawBase64,
          mimeType: pendingUploadImage.mimeType,
        });
        imageUrl = response.url;
      } catch (err) {
        logger.warn('Storage upload failed, using data URI fallback');
      }
    }

    const currentSlides = slidesRef.current;
    const slideIndex = currentSlides.findIndex(s => s.id === slideId);

    if (slideIndex !== -1) {
      const slide = currentSlides[slideIndex];
      const newSlides = [...currentSlides];
      const isStoryteller = style === CarouselStyle.STORYTELLER;
      newSlides[slideIndex] = {
        ...slide,
        showImage: true,
        imageUrl: withImageCacheBust(imageUrl),
        imageScale: slide.imageScale || (isStoryteller ? 45 : 50),
        overlayImage: isStoryteller ? true : undefined
      };
      onUpdateSlides(newSlides);
    } else {
      logger.warn('Slide was deleted before applying uploaded image');
    }

    // Reset modal state
    setShowUploadModal(false);
    setPendingUploadImage(null);
    setStylizePrompt('');
  };

  const handleStylizeImage = async () => {
    if (!pendingUploadImage || !stylizePrompt.trim() || !activeSlide) return;

    // Capture slide ID at call time
    const slideId = activeSlide.id;

    setIsStylizing(true);
    try {
      // Use the detected ratio from the original image to preserve proportions
      const stylizedImage = await stylizeImage(
        stylizePrompt,
        pendingUploadImage.detectedRatio,  // Use detected, not global aspect ratio
        DEFAULT_SLIDE_IMAGE_MODEL,
        projectId,
        activeSlide?.id,
        { imageBase64: pendingUploadImage.rawBase64, mimeType: pendingUploadImage.mimeType },
      );

      // Use ref to get latest slides (avoids stale closure)
      const currentSlides = slidesRef.current;
      const slideIndex = currentSlides.findIndex(s => s.id === slideId);

      // Handle case where slide was deleted during stylization
      if (slideIndex === -1) {
        logger.warn('Slide was deleted during image stylization');
        return;
      }

      const slide = currentSlides[slideIndex];
      const newSlides = [...currentSlides];
      const isStoryteller = style === CarouselStyle.STORYTELLER;
      newSlides[slideIndex] = {
        ...slide,
        showImage: true,
        imageUrl: withImageCacheBust(stylizedImage),
        imageScale: slide.imageScale || (isStoryteller ? 45 : 50),
        overlayImage: isStoryteller ? true : undefined
      };
      onUpdateSlides(newSlides);

      // Reset modal state
      setShowUploadModal(false);
      setPendingUploadImage(null);
      setStylizePrompt('');
    } catch (error: any) {
      logger.error('Stylization failed');
      alert(t('workspace.errors.stylizeFailed', { detail: error?.message || t('errors.unknown') }));
    } finally {
      setIsStylizing(false);
    }
  };

  const handleCancelUpload = () => {
    setShowUploadModal(false);
    setPendingUploadImage(null);
    setStylizePrompt('');
  };

  // ============================================================================
  // AI IMAGE EDITING HANDLERS
  // ============================================================================

  /**
   * Opens the AI image edit modal for the current slide.
   */
  const handleOpenEditModal = () => {
    if (!activeSlide?.imageUrl) return;
    setShowEditImageModal(true);
  };

  /**
   * Closes the AI image edit modal and resets state.
   */
  const handleCloseEditModal = () => {
    setShowEditImageModal(false);
    setEditImagePrompt('');
  };

  /**
   * Applies AI editing to the current slide's image.
   *
   * ASYNC PATTERN: Same as stylizeImage - captures slideId early,
   * uses slidesRef for current state to avoid stale closures.
   */
  const handleEditImage = async () => {
    if (!activeSlide?.imageUrl || !editImagePrompt.trim()) return;

    // Capture slide ID at call time
    const slideId = activeSlide.id;
    const currentImageUrl = activeSlide.imageUrl;

    setIsEditingImage(true);
    try {
      const isStorageUrl = currentImageUrl.startsWith('http');
      let source: { imageUrl: string } | { imageBase64: string; mimeType: string };
      let ratioSourceUrl: string;

      if (isStorageUrl) {
        // Storage URL: send URL to backend (server-side download avoids nginx body size limits)
        source = { imageUrl: currentImageUrl };
        ratioSourceUrl = currentImageUrl;
      } else {
        // Data URI: compress and send base64 (fallback for non-Storage images)
        let workingDataUrl = currentImageUrl;

        let parsed = extractBase64FromDataUrl(workingDataUrl);
        if (!parsed) {
          throw new LocalEditImageError('Invalid image data format for editing', 'EDIT_IMAGE_PROCESS_FAILED');
        }

        let nextMaxDimension = EDIT_IMAGE_INITIAL_MAX_DIMENSION;
        let nextQuality = EDIT_IMAGE_INITIAL_QUALITY;
        let attempt = 0;

        while (
          parsed.base64.length > EDIT_IMAGE_MAX_BASE64_LENGTH &&
          attempt < EDIT_IMAGE_MAX_COMPRESSION_ATTEMPTS
        ) {
          workingDataUrl = await downscaleImageDataUrl(workingDataUrl, nextMaxDimension, nextQuality);
          parsed = extractBase64FromDataUrl(workingDataUrl);
          if (!parsed) {
            throw new LocalEditImageError('Failed to re-encode image for editing', 'EDIT_IMAGE_PROCESS_FAILED');
          }
          nextMaxDimension = Math.max(EDIT_IMAGE_MIN_DIMENSION, Math.round(nextMaxDimension * 0.8));
          nextQuality = Math.max(EDIT_IMAGE_MIN_QUALITY, Number((nextQuality - 0.1).toFixed(2)));
          attempt += 1;
        }

        if (parsed.base64.length > EDIT_IMAGE_MAX_BASE64_LENGTH) {
          throw new LocalEditImageError('Image is too large for editing payload', 'EDIT_IMAGE_TOO_LARGE');
        }

        source = { imageBase64: parsed.base64, mimeType: parsed.mimeType || 'image/jpeg' };
        ratioSourceUrl = workingDataUrl;
      }

      // Detect aspect ratio from current image
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.src = ratioSourceUrl;
      await new Promise<void>((resolve, reject) => {
        if (img.complete && img.naturalWidth > 0) resolve();
        else {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Failed to load image for editing'));
        }
      });
      const ratio = img.naturalWidth / img.naturalHeight;
      const detectedRatio = getClosestApiRatio(ratio);

      const editedImage = await editImage(
        editImagePrompt,
        detectedRatio,
        DEFAULT_SLIDE_IMAGE_MODEL,
        projectId,
        slideId,
        source,
      );

      // Use ref to get latest slides (avoids stale closure)
      const currentSlides = slidesRef.current;
      const slideIndex = currentSlides.findIndex(s => s.id === slideId);

      // Handle case where slide was deleted during editing
      if (slideIndex === -1) {
        logger.warn('Slide was deleted during image editing');
        return;
      }

      const slide = currentSlides[slideIndex];
      const newSlides = [...currentSlides];
      newSlides[slideIndex] = {
        ...slide,
        imageUrl: withImageCacheBust(editedImage)
      };
      onUpdateSlides(newSlides);

      // Close modal and reset
      setShowEditImageModal(false);
      setEditImagePrompt('');
    } catch (error: any) {
      logger.error('Image editing failed');
      let detail = error?.message || t('errors.unknown');

      if (error instanceof LocalEditImageError) {
        if (error.code === 'EDIT_IMAGE_TOO_LARGE') {
          detail = t('workspace.errors.editImageTooLarge');
        } else {
          detail = t('workspace.errors.editImageProcessFailed');
        }
      } else if (error instanceof ApiError && (error.status === 429 || error.status === 503 || error.status === 504)) {
        detail = t('workspace.errors.editImageRetryTransient');
      }

      alert(t('workspace.errors.editFailed', { detail }));
    } finally {
      setIsEditingImage(false);
    }
  };

  // ============================================================================
  // AI CONTENT REFINEMENT HANDLERS
  // ============================================================================

  /**
   * Refines all slides based on global feedback.
   * E.g., "Translate to Spanish", "Make more technical", "Make it more fun"
   */
  const handleGlobalRefine = async () => {
    if (!globalFeedback.trim() || isRefining) return;

    setIsRefining(true);
    try {
      const refinedSlides = await refineCarouselContent(slides, globalFeedback, undefined, undefined, language);
      onUpdateSlides(refinedSlides);
      setGlobalFeedback('');
    } catch (error: any) {
      logger.error('Failed to refine content');
      alert(t('workspace.errors.refineFailed', { detail: error?.message || t('errors.unknown') }));
    } finally {
      setIsRefining(false);
    }
  };

  /**
   * Refines a single slide based on per-slide feedback.
   */
  const handleSlideRefine = async () => {
    if (!slideFeedback.trim() || isRefining || !activeSlide) return;

    setIsRefining(true);
    try {
      const refinedSlides = await refineCarouselContent(slides, slideFeedback, activeIndex, undefined, language);
      onUpdateSlides(refinedSlides);
      setSlideFeedback('');
    } catch (error: any) {
      logger.error('Failed to refine slide');
      alert(t('workspace.errors.refineSlideFailed', { detail: error?.message || t('errors.unknown') }));
    } finally {
      setIsRefining(false);
    }
  };

  // ============================================================================
  // PROJECT EXPORT/IMPORT HANDLERS
  // ============================================================================

  const projectImportRef = useRef<HTMLInputElement>(null);
  const canvasViewportRef = useRef<HTMLDivElement>(null);

  /**
   * Exports the current carousel project as a JSON file.
   * Includes all slides, profile, and global settings.
   */
  const handleExportProject = () => {
    const project: CarouselProject = {
      id: generateId(),
      name: `carousel-${new Date().toISOString().split('T')[0]}`,
      style,
      aspectRatio,
      profile,
      slides,
      theme,
      accentColor,
      showAccent,
      showSlideNumbers,
      showVerifiedBadge,
      headerScale,
      fontStyle,
      fontScale,
      globalImageStyle,
      layoutSettings,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
    window.saveAs(blob, `${project.name}.json`);
  };

  /**
   * Imports a carousel project from a JSON file.
   * Restores all slides and global settings.
   */
  const handleImportProject = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const project = JSON.parse(event.target?.result as string) as CarouselProject;

        // Validate required fields
        if (!project.slides || !Array.isArray(project.slides)) {
          throw new Error('Invalid project file: missing slides');
        }

        // Restore slides
        onUpdateSlides(project.slides);

        // Restore global settings (with fallbacks for older project files)
        if (project.theme) setTheme(project.theme);
        if (project.accentColor) setAccentColor(project.accentColor);
        if (project.showAccent !== undefined) setShowAccent(project.showAccent);
        if (project.showSlideNumbers !== undefined) setShowSlideNumbers(project.showSlideNumbers);
        if (project.showVerifiedBadge !== undefined) setShowVerifiedBadge(project.showVerifiedBadge);
        if (project.headerScale) setHeaderScale(project.headerScale);
        if (project.fontStyle) setFontStyle(project.fontStyle);
        if (project.fontScale) setFontScale(project.fontScale);
        if (project.globalImageStyle) setGlobalImageStyle(project.globalImageStyle);
        if (project.layoutSettings) setLayoutSettings(project.layoutSettings);

        // Set active slide to first slide
        if (project.slides.length > 0) {
          setActiveSlideId(project.slides[0].id);
        }

        alert(t('workspace.errors.importSuccess'));
      } catch (error) {
        logger.error('Failed to import project');
        alert(t('workspace.errors.importFailed'));
      }
    };

    reader.readAsText(file);

    // Reset input so same file can be imported again
    if (e.target) e.target.value = '';
  };

  // ============================================================================
  // STYLE CONVERSION HANDLER
  // ============================================================================

  /**
   * Converts the carousel between Twitter and Storyteller styles.
   * Adjusts slide properties for optimal display in the new style.
   */
  const handleConvertStyle = (newStyle: CarouselStyle) => {
    if (!onStyleChange || newStyle === style) return;

    // Adjust slide properties for the new style
    const convertedSlides = slides.map(slide => ({
      ...slide,
      // Reset style-specific layout properties
      overlayImage: newStyle === CarouselStyle.STORYTELLER ? true : undefined,
      imageScale: newStyle === CarouselStyle.STORYTELLER ? 45 : 50,
      contentLayout: undefined, // Reset to default for new style
    }));

    onUpdateSlides(convertedSlides);
    onStyleChange(newStyle);
  };

  // ============================================================================
  // IMAGE GENERATION HANDLERS
  // ============================================================================

  /**
   * Generates an AI image for the currently active slide.
   *
   * ASYNC PATTERN: Captures slideId early, uses slidesRef for current state.
   * This allows concurrent generation on multiple slides without data loss.
   */
  const handleGenerateImage = async () => {
    if (!activeSlide) return;

    // Capture slide ID and prompt at call time (not the whole slide object)
    // This ensures we update the correct slide even if user switches slides during generation
    const slideId = activeSlide.id;
    const prompt = activeSlide.imagePrompt || buildFallbackImagePrompt(activeSlide.content);

    // Add this slide to generating set (allows concurrent generation)
    setGeneratingSlideIds(prev => new Set(prev).add(slideId));

    try {
      const imageResult = await generateSlideImage(
        prompt,
        DEFAULT_SLIDE_IMAGE_RATIO,
        DEFAULT_GENERATE_IMAGE_MODEL,
        globalImageStyle,
        projectId,
        slideId,
        'slide'
      );

      // Use ref to get latest slides (avoids stale closure)
      const currentSlides = slidesRef.current;
      const slideIndex = currentSlides.findIndex(s => s.id === slideId);

      // Handle case where slide was deleted during generation
      if (slideIndex === -1) {
        logger.warn('Slide was deleted during image generation');
        return;
      }

      const slide = currentSlides[slideIndex];
      const newSlides = [...currentSlides];
      const isStoryteller = style === CarouselStyle.STORYTELLER;
      newSlides[slideIndex] = {
        ...slide,
        showImage: true,
        imageUrl: withImageCacheBust(imageResult),
        imageScale: slide.imageScale || (isStoryteller ? 45 : 50),
        overlayImage: isStoryteller ? true : undefined
      };
      onUpdateSlides(newSlides);
    } catch (error: any) {
      logger.error('Image generation failed');
      let detail = error?.message || t('errors.unknown');
      if (error instanceof ApiError && (error.code === 'UPSTREAM_TIMEOUT' || error.status === 504)) {
        detail = t('workspace.errors.imageRetryTimeout');
      } else if (error instanceof ApiError && (error.status === 429 || error.status === 503)) {
        detail = t('workspace.errors.imageRetryTransient');
      }
      alert(t('workspace.errors.imageFailed', { detail }));
    } finally {
      // Remove this slide from generating set
      setGeneratingSlideIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(slideId);
        return newSet;
      });
    }
  };

  /**
   * Generates a background image for the current slide using AI.
   * Uses user-provided prompt or falls back to slide content.
   * Background images are full-bleed and work with a color overlay.
   */
  const handleGenerateBackgroundImage = async () => {
    if (!activeSlide) return;

    const slideId = activeSlide.id;

    // Use user prompt if provided, otherwise create one from slide content
    let prompt: string;
    if (backgroundPrompt.trim()) {
      prompt = backgroundPrompt.trim();
    } else {
      const contentSnippet = activeSlide.content.substring(0, 100).replace(/[#*_~]/g, '');
      prompt = `Abstract atmospheric background for: ${contentSnippet}. Soft lighting, blurred details, suitable as text background.`;
    }

    setGeneratingBackgroundIds(prev => new Set(prev).add(slideId));

    try {
      // Use 1:1 aspect ratio for backgrounds (works well with any slide ratio)
      const bgImageResult = await generateSlideImage(
        prompt,
        '1/1',
        DEFAULT_GENERATE_IMAGE_MODEL,
        globalImageStyle,
        projectId,
        slideId,
        'background'
      );

      const currentSlides = slidesRef.current;
      const slideIndex = currentSlides.findIndex(s => s.id === slideId);

      if (slideIndex === -1) {
        logger.warn('Slide was deleted during background image generation');
        return;
      }

      const slide = currentSlides[slideIndex];
      const newSlides = [...currentSlides];
      newSlides[slideIndex] = {
        ...slide,
        showBackgroundImage: true,
        backgroundImageUrl: bgImageResult
      };
      onUpdateSlides(newSlides);
    } catch (error: any) {
      logger.error('Background image generation failed');
      alert(t('workspace.errors.backgroundImageFailed', { detail: error?.message || t('errors.unknown') }));
    } finally {
      setGeneratingBackgroundIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(slideId);
        return newSet;
      });
    }
  };

  // Capture globalImageStyle at call time to avoid stale closure in batch generation
  const globalImageStyleRef = useRef(globalImageStyle);
  useEffect(() => {
    globalImageStyleRef.current = globalImageStyle;
  }, [globalImageStyle]);

  /**
   * Generates images for multiple slides simultaneously.
   *
   * PATTERN: Promise.allSettled for parallel operations
   * - All selected slides generate images concurrently
   * - Individual failures don't stop other generations
   * - Per-slide status tracking shows progress in UI
   *
   * FLOW:
   * 1. Set all selected slides to "generating" status
   * 2. Fire off all generation requests in parallel
   * 3. Wait for ALL to complete (success or failure)
   * 4. Update slides with successful images
   * 5. Show success/error status for 3 seconds, then reset
   */
  const handleBatchGenerateImages = async () => {
    if (selectedSlideIds.size === 0) return;

    setBatchGenerating(true);

    // Capture the current image style at call time to use in all promises
    const currentImageStyle = globalImageStyleRef.current;

    // Initialize all selected slides as "generating" for UI feedback
    const initialStatus: Record<string, 'idle' | 'generating' | 'success' | 'error'> = {};
    selectedSlideIds.forEach(id => {
      initialStatus[id] = 'generating';
    });
    setSlideGenerationStatus(initialStatus);

    // Create array of generation promises (all run concurrently)
    const generationPromises = Array.from(selectedSlideIds).map(async (slideId) => {
      const slideIndex = slides.findIndex(s => s.id === slideId);
      if (slideIndex === -1) return { slideId, success: false };

      const slide = slides[slideIndex];
      const prompt = slide.imagePrompt || buildFallbackImagePrompt(slide.content);

      try {
        const imageResult = await generateSlideImage(
          prompt,
          DEFAULT_SLIDE_IMAGE_RATIO,
          DEFAULT_GENERATE_IMAGE_MODEL,
          currentImageStyle,
          projectId,
          slideId,
          'slide'
        );
        return { slideId, success: true, imageUrl: imageResult };
      } catch (error) {
        logger.error('Failed to generate image for slide');
        return { slideId, success: false };
      }
    });

    // Execute all in parallel using Promise.allSettled
    const results = await Promise.allSettled(generationPromises);

    // Process results and update slides
    const newSlides = [...slides];
    const newStatus: Record<string, 'idle' | 'generating' | 'success' | 'error'> = {};
    const isStoryteller = style === CarouselStyle.STORYTELLER;

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        const { slideId, success, imageUrl } = result.value as { slideId: string; success: boolean; imageUrl?: string };
        newStatus[slideId] = success ? 'success' : 'error';

        if (success && imageUrl) {
          const slideIndex = newSlides.findIndex(s => s.id === slideId);
          if (slideIndex !== -1) {
            newSlides[slideIndex] = {
              ...newSlides[slideIndex],
              showImage: true,
              imageUrl: withImageCacheBust(imageUrl),
              imageScale: newSlides[slideIndex].imageScale || (isStoryteller ? 45 : 50),
              overlayImage: isStoryteller ? true : undefined
            };
          }
        }
      }
    });

    onUpdateSlides(newSlides);
    setSlideGenerationStatus(newStatus);
    setBatchGenerating(false);

    // Clear status after 3 seconds
    setTimeout(() => {
      setSlideGenerationStatus({});
      setSelectedSlideIds(new Set());
      setBatchMode(false);
    }, 3000);
  };

  const toggleSlideSelection = (slideId: string) => {
    setSelectedSlideIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slideId)) {
        newSet.delete(slideId);
      } else {
        newSet.add(slideId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedSlideIds.size === slides.length) {
      setSelectedSlideIds(new Set());
    } else {
      setSelectedSlideIds(new Set(slides.map(s => s.id)));
    }
  };

  // ============================================================================
  // PNG EXPORT (Download Logic)
  // ============================================================================

  /**
   * Pre-loads and decodes all image URLs from the given slides into the
   * browser's HTTP cache. This ensures html-to-image can render them
   * instantly from cache instead of re-fetching over the network.
   */
  const preloadAllImages = async (slidesToExport: Slide[]): Promise<void> => {
    const urls = new Set<string>();

    slidesToExport.forEach(slide => {
      if (slide.imageUrl && !slide.imageUrl.startsWith('data:')) urls.add(slide.imageUrl);
      if (slide.backgroundImageUrl && !slide.backgroundImageUrl.startsWith('data:')) urls.add(slide.backgroundImageUrl);
    });
    if (profile.avatarUrl && !profile.avatarUrl.startsWith('data:')) urls.add(profile.avatarUrl);

    if (urls.size === 0) return;

    await Promise.all([...urls].map(url =>
      new Promise<void>((resolve) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          img.decode().then(() => resolve()).catch(() => resolve());
        };
        img.onerror = () => resolve();
        img.src = url;
        setTimeout(resolve, 12000);
      })
    ));
  };

  /**
   * Polls via requestAnimationFrame until the preview element has rendered
   * the expected slide. When expectedSlideId is provided, waits until the
   * element's data-slide-id attribute matches, ensuring React has actually
   * re-rendered with the new slide content (not stale content from the
   * previous slide which would satisfy a simple innerHTML check).
   */
  const waitForDOMStable = (elementId: string, expectedSlideId?: string, maxWaitMs = 3000): Promise<HTMLElement | null> => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const check = () => {
        const el = document.getElementById(elementId);
        if (el && el.innerHTML.length > 0) {
          if (!expectedSlideId || el.getAttribute('data-slide-id') === expectedSlideId) {
            resolve(el);
            return;
          }
        }
        if (Date.now() - startTime > maxWaitMs) {
          resolve(el);
          return;
        }
        requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    });
  };

  /**
   * Waits for all <img> elements within an element to finish loading.
   * Uses a resolved flag to prevent the promise from resolving multiple times.
   */
  const waitForImages = (element: HTMLElement): Promise<void> => {
    return new Promise((resolve) => {
      let resolved = false;
      const done = () => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      };

      const images = element.querySelectorAll('img');

      if (images.length === 0) {
        done();
        return;
      }

      let loaded = 0;
      const total = images.length;

      const checkDone = () => {
        loaded++;
        if (loaded >= total) done();
      };

      images.forEach((img) => {
        if (img.complete && img.naturalHeight > 0) {
          img.decode().then(checkDone).catch(checkDone);
        } else {
          img.onload = () => img.decode().then(checkDone).catch(checkDone);
          img.onerror = checkDone;
        }
      });

      // Safety timeout (10s to accommodate slower connections/devices)
      setTimeout(done, 10000);
    });
  };

  /**
   * html2canvas does not support CSS object-fit. This function pre-crops every
   * <img> with object-fit:cover using a temporary canvas so that the resulting
   * data-URI already has the correct aspect ratio. After capture, call
   * restoreObjectFitImages() to put the originals back.
   */
  const preProcessObjectFitImages = (
    root: HTMLElement
  ): Map<HTMLImageElement, { src: string; objectFit: string; objectPosition: string }> => {
    const originals = new Map<HTMLImageElement, { src: string; objectFit: string; objectPosition: string }>();

    root.querySelectorAll('img').forEach((img) => {
      const computed = window.getComputedStyle(img);
      if (computed.objectFit !== 'cover') return;
      if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) return;

      const cw = img.clientWidth;
      const ch = img.clientHeight;
      if (cw === 0 || ch === 0) return;

      const imgRatio = img.naturalWidth / img.naturalHeight;
      const containerRatio = cw / ch;

      let sx: number, sy: number, sw: number, sh: number;

      if (imgRatio > containerRatio) {
        sh = img.naturalHeight;
        sw = sh * containerRatio;
        sx = (img.naturalWidth - sw) / 2;
        sy = 0;
      } else {
        sw = img.naturalWidth;
        sh = sw / containerRatio;
        sx = 0;
        sy = (img.naturalHeight - sh) / 2;
      }

      const objPos = computed.objectPosition || '50% 50%';
      const parts = objPos.split(/\s+/);
      const vPart = parts.length >= 2 ? parts[1] : '50%';
      if (vPart.endsWith('%')) {
        const pct = parseFloat(vPart) / 100;
        if (imgRatio <= containerRatio) {
          const maxSy = img.naturalHeight - sh;
          sy = maxSy * pct;
        }
      }
      const hPart = parts[0] || '50%';
      if (hPart.endsWith('%')) {
        const pct = parseFloat(hPart) / 100;
        if (imgRatio > containerRatio) {
          const maxSx = img.naturalWidth - sw;
          sx = maxSx * pct;
        }
      }

      const tmpCanvas = document.createElement('canvas');
      tmpCanvas.width = cw;
      tmpCanvas.height = ch;
      const ctx = tmpCanvas.getContext('2d');
      if (!ctx) return;

      try {
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
      } catch {
        return;
      }

      originals.set(img, {
        src: img.src,
        objectFit: img.style.objectFit || '',
        objectPosition: img.style.objectPosition || '',
      });

      img.src = tmpCanvas.toDataURL('image/png');
      img.style.objectFit = 'fill';
      img.style.objectPosition = 'center center';

      tmpCanvas.width = 0;
      tmpCanvas.height = 0;
    });

    return originals;
  };

  const restoreObjectFitImages = (
    originals: Map<HTMLImageElement, { src: string; objectFit: string; objectPosition: string }>
  ) => {
    originals.forEach(({ src, objectFit, objectPosition }, img) => {
      img.src = src;
      img.style.objectFit = objectFit;
      img.style.objectPosition = objectPosition;
    });
  };

  /**
   * iOS html2canvas text displacement fix.
   * html2canvas on iOS WebKit interprets unitless CSS line-height values
   * differently from the live renderer, causing text to shift vertically.
   * This converts every unitless line-height to an explicit pixel value
   * (e.g., lineHeight: 1.5 + fontSize: 48px → lineHeight: "72px"),
   * removing the ambiguity. After capture, call restoreTextAfterIOSCapture()
   * to put the original values back.
   */
  const preProcessTextForIOSCapture = (
    root: HTMLElement
  ): Map<HTMLElement, { lineHeight: string; fontSize: string }> => {
    const originals = new Map<HTMLElement, { lineHeight: string; fontSize: string }>();

    const textElements = root.querySelectorAll<HTMLElement>(
      'h1, h2, h3, p, span, div, strong, em, s, u, a'
    );

    textElements.forEach((el) => {
      const computed = window.getComputedStyle(el);

      // Skip flex/grid containers — they don't render text directly
      // and setting lineHeight/fontSize on them confuses html2canvas's box model
      if (
        el.tagName === 'DIV' &&
        (computed.display === 'flex' || computed.display === 'inline-flex' ||
         computed.display === 'grid' || computed.display === 'inline-grid')
      ) {
        return;
      }

      const fontSize = parseFloat(computed.fontSize);
      const lineHeight = computed.lineHeight;

      originals.set(el, {
        lineHeight: el.style.lineHeight || '',
        fontSize: el.style.fontSize || '',
      });

      // Use Math.floor to avoid rounding up, which would create line boxes
      // larger than the browser's true computed value and cause cumulative
      // downward drift in html2canvas.
      if (lineHeight === 'normal') {
        el.style.lineHeight = `${Math.floor(fontSize * 1.2)}px`;
      } else {
        const lhPx = parseFloat(lineHeight);
        if (!isNaN(lhPx) && lhPx > 0) {
          el.style.lineHeight = `${Math.floor(lhPx)}px`;
        }
      }

      if (!isNaN(fontSize) && fontSize > 0) {
        el.style.fontSize = `${fontSize}px`;
      }
    });

    return originals;
  };

  const restoreTextAfterIOSCapture = (
    originals: Map<HTMLElement, { lineHeight: string; fontSize: string }>
  ) => {
    originals.forEach(({ lineHeight, fontSize }, el) => {
      el.style.lineHeight = lineHeight;
      el.style.fontSize = fontSize;
    });
  };

  /**
   * Captures the visible preview element as a PNG image.
   * When imageCache is provided (batch export), uses the pre-fetched data URIs
   * to replace external URLs instantly — no per-slide CORS fetches needed.
   * When not provided (single download), falls back to inlineExternalImages.
   * On iOS WebKit, uses html2canvas (Canvas 2D API) to avoid SVG foreignObject
   * canvas tainting. Falls back to html-to-image on desktop.
   */
  const captureSlide = async (imageCache?: Map<string, string>): Promise<Blob | null> => {
    const element = isMobile
      ? document.getElementById('preview-slide-capture-mobile')
      : document.getElementById('preview-slide-capture');
    if (!element) return null;

    await waitForImages(element);
    const bgColor = theme === 'DARK' ? '#0a0a0a' : '#FFFFFF';

    // Batch mode: instant replacement from pre-fetched cache (no network)
    // Single mode: per-image CORS fetch (fine for one-off captures)
    const inlinedImages = imageCache
      ? inlineImagesFromCache(element, imageCache)
      : await inlineExternalImages(element);

    await waitForImages(element);

    // PRIMARY (all platforms, including iOS): html-to-image. The SVG
    // foreignObject is rendered by the browser's own layout engine, so
    // exported text (handle, name, badge, page numbers) matches the on-screen
    // preview exactly. html2canvas re-implements CSS layout and drifts text
    // baselines a few px downward on iOS WebKit, so it is only a fallback.
    // The verify+retry loop covers WebKit's decode race (blank image regions
    // on a cold first capture).
    const htmlToImageCapture = async (): Promise<Blob | null> => {
      try {
        element.classList.add('capturing-export');
        // Embed the actual web fonts so serif/display fonts (e.g. Playfair) render
        // correctly instead of falling back to system fonts and overflowing.
        try { await (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready; } catch { /* noop */ }
        const fontEmbedCSS = await getFontEmbedCSS(collectUsedFontFaces([element]));
        let blob: Blob | null = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          const dataUrl = await window.htmlToImage.toPng(element, {
            width: PREVIEW_WIDTH,
            height: previewHeight,
            backgroundColor: bgColor,
            pixelRatio: 1,
            ...(fontEmbedCSS ? { fontEmbedCSS } : { skipFonts: true }),
            imagePlaceholder: TRANSPARENT_PIXEL,
          });
          const response = await fetch(dataUrl);
          blob = await response.blob();
          if (blob && !(await hasBlankImageRegions(blob, element))) break;
        }
        element.classList.remove('capturing-export');
        return blob;
      } catch {
        element.classList.remove('capturing-export');
        logger.error('Capture failed');
        return null;
      }
    };

    const primaryBlob = await htmlToImageCapture();
    if (primaryBlob && !(await hasBlankImageRegions(primaryBlob, element))) {
      restoreOriginalImages(inlinedImages);
      return primaryBlob;
    }

    // FALLBACK (iOS only): html2canvas (Canvas 2D API, no SVG foreignObject),
    // in case html-to-image kept producing blank image regions even after the
    // verified retries.
    // html2canvas has a known bug where it captures elements at their visual
    // (transformed) size instead of their logical size. The preview element sits
    // inside a parent with transform: scale(fitScale * zoomFactor), so we must
    // temporarily neutralize that transform before capture.
    if (isIOSWebKit() && window.html2canvas) {
      const parent = element.parentElement;
      const grandparent = parent?.parentElement;
      const savedTransform = parent?.style.transform || '';
      const savedTransition = parent?.style.transition || '';
      const savedOverflow = grandparent?.style.overflow;
      try {
        if (parent) {
          parent.style.transition = 'none';
          parent.style.transform = 'none';
        }
        if (grandparent) {
          grandparent.style.overflow = 'visible';
        }
        element.offsetHeight; // force reflow

        const objectFitOriginals = preProcessObjectFitImages(element);
        const textOriginals = preProcessTextForIOSCapture(element);

        element.classList.add('capturing-export');
        element.classList.add('capturing-export-ios');
        element.offsetHeight; // force reflow after text preprocessing
        const canvas = await window.html2canvas(element, {
          width: PREVIEW_WIDTH,
          height: previewHeight,
          backgroundColor: bgColor,
          scale: 1,
          useCORS: true,
          allowTaint: false,
          logging: false,
        });
        element.classList.remove('capturing-export');
        element.classList.remove('capturing-export-ios');

        restoreTextAfterIOSCapture(textOriginals);
        restoreObjectFitImages(objectFitOriginals);
        if (parent) {
          parent.style.transform = savedTransform;
          parent.style.transition = savedTransition;
        }
        if (grandparent) {
          grandparent.style.overflow = savedOverflow ?? '';
        }

        const iosBlob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(
            (blob: Blob | null) => {
              const ctx = canvas.getContext('2d');
              if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
              canvas.width = 0;
              canvas.height = 0;
              resolve(blob);
            },
            'image/png'
          );
        });
        // Only accept the html2canvas result when every image region actually
        // painted; otherwise fall out to return the html-to-image best effort.
        if (iosBlob && !(await hasBlankImageRegions(iosBlob, element))) {
          restoreOriginalImages(inlinedImages);
          return iosBlob;
        }
      } catch (err) {
        element.classList.remove('capturing-export');
        element.classList.remove('capturing-export-ios');
        if (parent) {
          parent.style.transform = savedTransform;
          parent.style.transition = savedTransition;
        }
        if (grandparent) {
          grandparent.style.overflow = savedOverflow ?? '';
        }
        logger.error('html2canvas fallback capture failed');
      }
    }

    // Both engines failed to produce a verified-clean capture; return the
    // html-to-image best effort (or null).
    restoreOriginalImages(inlinedImages);
    return primaryBlob;
  };

  /**
   * Robust slide capture with retry logic.
   * If the captured blob is suspiciously small (< 5KB), it likely means
   * images haven't loaded yet, so we retry after a delay.
   */
  const captureSlideRobust = async (retries = 2, imageCache?: Map<string, string>): Promise<Blob | null> => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      const blob = await captureSlide(imageCache);
      if (blob && blob.size > 5000) return blob;
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    return await captureSlide(imageCache);
  };

  // ==========================================================================
  // THUMBNAIL GENERATION - Captures first slide for dashboard preview
  // ==========================================================================
  const thumbnailTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const thumbnailCallbackRef = useRef<((base64: string) => void) | null>(null);

  // Sync callback ref whenever prop changes (stable reference pattern)
  useEffect(() => {
    thumbnailCallbackRef.current = onThumbnailGenerated || null;
  }, [onThumbnailGenerated]);

  const generateThumbnail = async (): Promise<string | null> => {
    const element = document.getElementById('thumbnail-capture');

    if (!element || (!window.htmlToImage && !window.html2canvas)) {
      return null;
    }

    try {
      const htmlElement = element as HTMLElement;
      const originalLeft = htmlElement.style.left;
      const originalOpacity = htmlElement.style.opacity;
      const originalVisibility = htmlElement.style.visibility;
      const originalZIndex = htmlElement.style.zIndex;

      htmlElement.style.left = '0px';
      htmlElement.style.opacity = '1';
      htmlElement.style.visibility = 'visible';
      htmlElement.style.zIndex = '-1';

      await waitForImages(element);
      const inlinedImages = await inlineExternalImages(element);
      await new Promise(resolve => setTimeout(resolve, 300));

      const bgColor = theme === 'DARK' ? '#0a0a0a' : '#FFFFFF';
      let pngDataUrl: string | null = null;
      let pngVerifiedClean = false;

      // PRIMARY (all platforms): html-to-image — native text layout, plus
      // verify+retry against WebKit's decode race (blank image regions).
      if (window.htmlToImage) {
        try {
          try { await (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready; } catch { /* noop */ }
          const fontEmbedCSS = await getFontEmbedCSS(collectUsedFontFaces([htmlElement]));
          for (let attempt = 0; attempt < 3; attempt++) {
            pngDataUrl = await window.htmlToImage.toPng(element, {
              width: PREVIEW_WIDTH,
              height: previewHeight,
              backgroundColor: bgColor,
              pixelRatio: 1.0,
              ...(fontEmbedCSS ? { fontEmbedCSS } : { skipFonts: true }),
              imagePlaceholder: TRANSPARENT_PIXEL,
            });
            if (pngDataUrl) {
              const blob = await (await fetch(pngDataUrl)).blob();
              if (!(await hasBlankImageRegions(blob, htmlElement))) {
                pngVerifiedClean = true;
                break;
              }
            }
          }
        } catch (err) {
          logger.error('html-to-image thumbnail failed, falling back to html2canvas');
          pngDataUrl = null;
        }
      }

      // FALLBACK (iOS only): html2canvas, when html-to-image kept producing
      // blank image regions even after the verified retries.
      if (!pngVerifiedClean && isIOSWebKit() && window.html2canvas) {
        const objectFitOriginals = preProcessObjectFitImages(htmlElement);
        const textOriginals = preProcessTextForIOSCapture(htmlElement);
        htmlElement.classList.add('capturing-export-ios');
        htmlElement.offsetHeight; // force reflow after text preprocessing
        try {
          const canvas = await window.html2canvas(element, {
            width: PREVIEW_WIDTH,
            height: previewHeight,
            backgroundColor: bgColor,
            scale: 1,
            useCORS: true,
            allowTaint: false,
            logging: false,
          });
          pngDataUrl = canvas.toDataURL('image/png');
          const ctx = canvas.getContext('2d');
          if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
          canvas.width = 0;
          canvas.height = 0;
        } catch (err) {
          logger.error('html2canvas thumbnail failed, falling back to html-to-image');
        }
        htmlElement.classList.remove('capturing-export-ios');
        restoreTextAfterIOSCapture(textOriginals);
        restoreObjectFitImages(objectFitOriginals);
      }

      // Desktop / fallback: html-to-image
      if (!pngDataUrl && window.htmlToImage) {
        try { await (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready; } catch { /* noop */ }
        const fontEmbedCSS = await getFontEmbedCSS(collectUsedFontFaces([htmlElement]));
        pngDataUrl = await window.htmlToImage.toPng(element, {
          width: PREVIEW_WIDTH,
          height: previewHeight,
          backgroundColor: bgColor,
          pixelRatio: 1.0,
          ...(fontEmbedCSS ? { fontEmbedCSS } : { skipFonts: true }),
          imagePlaceholder: TRANSPARENT_PIXEL,
        });
      }

      restoreOriginalImages(inlinedImages);

      htmlElement.style.left = originalLeft || '-2000px';
      htmlElement.style.opacity = originalOpacity || '0';
      htmlElement.style.visibility = originalVisibility || 'hidden';
      htmlElement.style.zIndex = originalZIndex || 'auto';

      if (!pngDataUrl) return null;

      const compressedDataUrl = await compressThumbnail(pngDataUrl);
      if (!compressedDataUrl) {
        logger.warn('Thumbnail compression failed');
        return null;
      }

      // Upload compressed thumbnail to Storage if projectId is available
      if (projectId) {
        try {
          const parsed = parseDataUrl(compressedDataUrl);
          if (!parsed) throw new Error('Failed to parse compressed thumbnail data URL');
          const response = await apiPost<{ url: string }>('/api/images/upload', {
            projectId,
            imageType: 'thumbnail',
            base64: parsed.base64,
            mimeType: parsed.mimeType,
          });
          return response.url;
        } catch (uploadErr) {
          logger.warn('Thumbnail upload to Storage failed, using compressed data URI');
        }
      }

      return compressedDataUrl;
    } catch (err) {
      logger.error('Thumbnail generation failed');

      // FAILSAFE: Ensure element is hidden and off-screen even if capture fails
      const element = document.getElementById('thumbnail-capture');
      if (element) {
        const htmlElement = element as HTMLElement;
        htmlElement.style.left = '-2000px';
        htmlElement.style.opacity = '0';
        htmlElement.style.visibility = 'hidden';
        htmlElement.style.zIndex = 'auto';
      }

      return null;
    }
  };

  useEffect(() => {
    if (!thumbnailCallbackRef.current || slides.length === 0) {
      return;
    }

    if (thumbnailTimeoutRef.current) {
      clearTimeout(thumbnailTimeoutRef.current);
    }

    thumbnailTimeoutRef.current = setTimeout(async () => {
      const thumbnail = await generateThumbnail();
      if (thumbnail && thumbnailCallbackRef.current) {
        thumbnailCallbackRef.current(thumbnail);
      }
    }, 1000);

    return () => {
      if (thumbnailTimeoutRef.current) {
        clearTimeout(thumbnailTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides, theme]); // REMOVED onThumbnailGenerated from dependencies

  const handleDownloadSlide = async (slideId?: string) => {
    if (isDownloading) return;
    setIsDownloading(true);

    const originalActiveId = activeSlideId;
    const targetSlideId = slideId || activeSlideId;
    let blob: Blob | null = null;

    if (targetSlideId !== activeSlideId) {
      await preloadAllImages(slides.filter(s => s.id === targetSlideId));
      setActiveSlideId(targetSlideId);

      // Wait for React re-render + DOM to stabilize with new slide content
      const previewId = isMobile ? 'preview-slide-capture-mobile' : 'preview-slide-capture';
      await waitForDOMStable(previewId, targetSlideId);
      await new Promise(resolve => setTimeout(resolve, isIOSWebKit() ? 2000 : (isMobile ? 1000 : 500)));

      blob = await captureSlideRobust();
      setActiveSlideId(originalActiveId);
    } else {
      blob = await captureSlideRobust();
    }

    if (blob) {
      const targetIndex = slides.findIndex(s => s.id === targetSlideId);
      downloadBlobForPlatform(blob, `slide-${targetIndex + 1}.png`);
    } else {
      alert(t('workspace.errors.generateImageTryAgain'));
    }
    setIsDownloading(false);
  };

  /**
   * Collects all unique external image URLs from a set of slides + profile avatar.
   */
  const collectExternalImageUrls = (slidesToScan: Slide[]): Set<string> => {
    const urls = new Set<string>();
    slidesToScan.forEach(slide => {
      if (slide.imageUrl && !slide.imageUrl.startsWith('data:')) urls.add(slide.imageUrl);
      if (slide.backgroundImageUrl && !slide.backgroundImageUrl.startsWith('data:')) urls.add(slide.backgroundImageUrl);
    });
    if (profile.avatarUrl && !profile.avatarUrl.startsWith('data:')) urls.add(profile.avatarUrl);
    return urls;
  };

  const handleDownloadCarousel = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    const originalActiveId = activeSlideId;

    try {
      const zip = new window.JSZip();

      await preloadAllImages(slides);

      // Pre-fetch ALL external image URLs once into data URIs.
      // This avoids per-slide CORS fetches that iOS WebKit throttles/blocks
      // during rapid sequential captures.
      const imageCache = await prefetchAllImageUrls(collectExternalImageUrls(slides));

      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        setActiveSlideId(slide.id);

        const previewId = isMobile ? 'preview-slide-capture-mobile' : 'preview-slide-capture';
        await waitForDOMStable(previewId, slide.id);
        await new Promise(resolve => setTimeout(resolve, isIOSWebKit() ? 2000 : (isMobile ? 1000 : 500)));

        const blob = await captureSlideRobust(2, imageCache);
        if (blob) {
          zip.file(`slide-${i + 1}.png`, blob);
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      window.saveAs(content, "instagram-carousel.zip");
    } catch (err) {
      logger.error('Batch export failed');
      alert(t('workspace.errors.generateImageTryAgain'));
    } finally {
      setActiveSlideId(originalActiveId);
      setIsDownloading(false);
    }
  };

  // Auto-trigger batch export when EditorPage was opened with ?autoExport=1.
  // Fires exactly once per app session. The ref guard is intentionally set
  // BEFORE the async work starts so a StrictMode double-invocation (or any
  // cleanup/re-run) bails on the second pass instead of racing. No
  // `cancelled` flag is used: once the capture starts, we let it finish
  // even if the component unmounts, because the only caller triggers this
  // via ?autoExport=1 and would prefer a completed download over a silent
  // abort.
  const autoExportFiredRef = useRef(false);
  useEffect(() => {
    if (!autoExportOnMount) return;
    if (autoExportFiredRef.current) return;
    if (slides.length === 0) return;
    autoExportFiredRef.current = true;

    const waitForPreview = async () => {
      const previewId = isMobile ? 'preview-slide-capture-mobile' : 'preview-slide-capture';
      const deadline = Date.now() + 10000;
      while (Date.now() < deadline) {
        const el = document.getElementById(previewId);
        if (el && el.innerHTML.length > 0) return true;
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      return false;
    };

    (async () => {
      const ready = await waitForPreview();
      if (!ready) {
        logger.error('Auto-export: preview element never rendered (10s timeout)');
        return;
      }
      // Extra beat so newly-attached images get a chance to decode.
      await new Promise((resolve) => window.setTimeout(resolve, 500));
      try {
        await handleDownloadCarousel();
      } catch (err) {
        logger.error('Auto-export failed');
      }
    })();
  }, [autoExportOnMount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownloadSelectedSlides = async (slideIds: string[]) => {
    if (isDownloading || slideIds.length === 0) return;
    setIsDownloading(true);
    const originalActiveId = activeSlideId;

    try {
      const zip = new window.JSZip();

      const slidesToExport = slides.filter(s => slideIds.includes(s.id));
      await preloadAllImages(slidesToExport);

      const imageCache = await prefetchAllImageUrls(collectExternalImageUrls(slidesToExport));

      for (const slideId of slideIds) {
        setActiveSlideId(slideId);

        const previewId = isMobile ? 'preview-slide-capture-mobile' : 'preview-slide-capture';
        await waitForDOMStable(previewId, slideId);
        await new Promise(resolve => setTimeout(resolve, isIOSWebKit() ? 2000 : (isMobile ? 1000 : 500)));

        const blob = await captureSlideRobust(2, imageCache);
        if (blob) {
          const targetIndex = slides.findIndex((slide) => slide.id === slideId);
          zip.file(`slide-${targetIndex + 1}.png`, blob);
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      window.saveAs(content, "instagram-carousel-selected.zip");
    } catch (err) {
      logger.error('Selected slides export failed');
      alert(t('workspace.errors.generateImageTryAgain'));
    } finally {
      setActiveSlideId(originalActiveId);
      setIsDownloading(false);
    }
  };

  const toggleExportSlideSelection = (slideId: string) => {
    setSelectedExportSlideIds((prev) => {
      const next = new Set(prev);
      if (next.has(slideId)) {
        next.delete(slideId);
      } else {
        next.add(slideId);
      }
      return next;
    });
  };

  const handleSelectAllExportSlides = () => {
    setSelectedExportSlideIds(new Set(slides.map((slide) => slide.id)));
  };

  const handleClearExportSlides = () => {
    setSelectedExportSlideIds(new Set());
  };

  const handleConfirmExport = async () => {
    if (exportMode === 'all') {
      await handleDownloadCarousel();
      trackFunnelEvent('carousel_exported', { type: 'carousel', slide_count: slides.length }).catch(() => {});
      setShowExportPopover(false);
      return;
    }

    const orderedSelectedSlideIds = slides
      .filter((slide) => selectedExportSlideIds.has(slide.id))
      .map((slide) => slide.id);

    if (orderedSelectedSlideIds.length === 0) return;

    if (orderedSelectedSlideIds.length === 1) {
      await handleDownloadSlide(orderedSelectedSlideIds[0]);
    } else {
      await handleDownloadSelectedSlides(orderedSelectedSlideIds);
    }

    trackFunnelEvent('carousel_exported', { type: orderedSelectedSlideIds.length === 1 ? 'single' : 'selected', slide_count: orderedSelectedSlideIds.length }).catch(() => {});
    setShowExportPopover(false);
  };

  const insertMarkdown = (prefix: string, suffix: string = '') => {
    if (!textAreaRef.current) return;
    const textarea = textAreaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = activeSlide.content;
    const selectedText = text.substring(start, end);
    const newText = text.substring(0, start) + prefix + selectedText + suffix + text.substring(end);
    handleTextChange(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, end + prefix.length);
    }, 0);
  };

  const PREVIEW_WIDTH = EXPORT_WIDTH;
  const TRANSPARENT_PIXEL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
  const previewHeight = getExportHeight(aspectRatio);

  const renderSlide = (slide: Slide, idx: number, isExport: boolean) => {
    // Per-slide theme override (if set) takes precedence over global theme
    const effectiveTheme = slide.theme || theme;

    const commonProps = {
      slide: slide,
      profile: profile,
      index: idx,
      total: slides.length,
      showSlideNumbers: showSlideNumbers,
      slideNumberPosition: slideNumberPosition,
      showVerifiedBadge: showVerifiedBadge,
      headerScale: headerScale,
      theme: effectiveTheme,
      accentColor: showAccent ? accentColor : undefined,
      forExport: isExport,
      fontStyle: fontStyle,
      fontScale: fontScale,
      layoutSettings: layoutSettings
    };

    if (style === CarouselStyle.STORYTELLER) {
      return <StorytellerSlide {...commonProps} />;
    }
    return <TwitterSlide {...commonProps} />;
  };

  const getSaveBadgeLabel = () => {
    if (saveStatus === 'saving') return t('workspace.savingNow');
    if (saveStatus === 'saved') return t('workspace.savedNow');
    if (saveStatus === 'error') return t('workspace.saveError');
    return t('workspace.savedNow');
  };

  const styleOptions: Array<{ value: CarouselStyle; label: string }> = [
    { value: CarouselStyle.TWITTER, label: t('workspace.styleTwitter') },
    { value: CarouselStyle.STORYTELLER, label: t('workspace.styleStoryteller') },
  ];

  const fontOptions: Array<{ value: FontStyle; label: string }> = [
    { value: 'MODERN', label: t('workspace.fontModern') },
    { value: 'SERIF', label: t('workspace.fontSerif') },
    { value: 'TECH', label: t('workspace.fontTech') },
    { value: 'ELEGANT', label: t('workspace.fontElegant') },
    { value: 'BOLD', label: t('workspace.fontBold') },
    { value: 'MINIMAL', label: t('workspace.fontMinimal') },
    { value: 'HANDWRITTEN', label: t('workspace.fontHandwritten') },
  ];

  const slideNumberPositionOptions: Array<{ value: SlideNumberPosition; label: string }> = [
    { value: 'center', label: t('workspace.positionCenter') },
    { value: 'bottom-right', label: t('workspace.positionRight') },
  ];
  const batchLoadingDots = '.'.repeat(batchLoadingStep);
  const batchGeneratingLabel = `${t('workspace.generatingBase')}${batchLoadingDots}`;

  const getExportSlidePreviewText = (content: string) => {
    const cleaned = content
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/(\*\*|__|\*|_|~~|`)/g, '')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      .replace(/\s+/g, ' ')
      .trim();
    return cleaned || '...';
  };

  const isAllExportSlidesSelected = slides.length > 0 && selectedExportSlideIds.size === slides.length;
  const hasSelectedExportSlides = selectedExportSlideIds.size > 0;
  const MIN_CANVAS_ZOOM = 0.25;
  const MAX_CANVAS_ZOOM = 4.0;
  const ZOOM_STEP_BUTTON = 0.1;
  const clampCanvasZoomFactor = (value: number) => Math.min(MAX_CANVAS_ZOOM, Math.max(MIN_CANVAS_ZOOM, value));

  const [canvasViewportSize, setCanvasViewportSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const viewport = canvasViewportRef.current;
    if (!viewport) return;

    const updateViewportSize = () => {
      setCanvasViewportSize({
        width: viewport.clientWidth,
        height: viewport.clientHeight,
      });
    };

    updateViewportSize();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateViewportSize);
      observer.observe(viewport);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateViewportSize);
    return () => window.removeEventListener('resize', updateViewportSize);
  }, []);

  useEffect(() => {
    const { width, height } = canvasViewportSize;
    if (width <= 0 || height <= 0) return;

    const nextFitScale = Math.min(width / PREVIEW_WIDTH, height / previewHeight);
    if (Number.isFinite(nextFitScale) && nextFitScale > 0) {
      setFitScale(nextFitScale);
    }
  }, [canvasViewportSize, previewHeight]);

  const clampPanForScale = useCallback((nextPan: { x: number; y: number }, nextZoomFactor: number) => {
    const actualScale = fitScale * nextZoomFactor;
    const scaledWidth = PREVIEW_WIDTH * actualScale;
    const scaledHeight = previewHeight * actualScale;
    const maxPanX = Math.max(0, (scaledWidth - canvasViewportSize.width) / 2);
    const maxPanY = Math.max(0, (scaledHeight - canvasViewportSize.height) / 2);

    return {
      x: Math.min(maxPanX, Math.max(-maxPanX, nextPan.x)),
      y: Math.min(maxPanY, Math.max(-maxPanY, nextPan.y)),
    };
  }, [canvasViewportSize.height, canvasViewportSize.width, fitScale, previewHeight]);

  useEffect(() => {
    if (zoomFactor <= 1) {
      setPan({ x: 0, y: 0 });
      return;
    }
    setPan((prev) => clampPanForScale(prev, zoomFactor));
  }, [zoomFactor, fitScale, clampPanForScale]);

  const applyZoomAtPoint = useCallback((nextZoomFactorRaw: number, anchorX: number, anchorY: number) => {
    const nextZoomFactor = clampCanvasZoomFactor(nextZoomFactorRaw);
    const oldScale = fitScale * zoomFactor;
    const newScale = fitScale * nextZoomFactor;

    if (oldScale <= 0 || newScale <= 0) {
      setZoomFactor(nextZoomFactor);
      return;
    }

    const centerX = canvasViewportSize.width / 2;
    const centerY = canvasViewportSize.height / 2;
    const dx = anchorX - centerX;
    const dy = anchorY - centerY;

    const nextPan = {
      x: dx - ((dx - pan.x) * newScale) / oldScale,
      y: dy - ((dy - pan.y) * newScale) / oldScale,
    };

    setZoomFactor(nextZoomFactor);
    setPan(clampPanForScale(nextPan, nextZoomFactor));
  }, [canvasViewportSize.height, canvasViewportSize.width, clampPanForScale, fitScale, pan.x, pan.y, zoomFactor]);

  useEffect(() => {
    const viewport = canvasViewportRef.current;
    if (!viewport) return;

    const handleWheel = (event: WheelEvent) => {
      const rect = viewport.getBoundingClientRect();
      const anchorX = event.clientX - rect.left;
      const anchorY = event.clientY - rect.top;

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const deltaFactor = Math.exp(-event.deltaY * 0.002);
        applyZoomAtPoint(zoomFactor * deltaFactor, anchorX, anchorY);
        return;
      }

      event.preventDefault();
      setPan((prev) => {
        const nextPan = {
          x: prev.x - event.deltaX,
          y: prev.y - event.deltaY,
        };
        return clampPanForScale(nextPan, zoomFactor);
      });
    };

    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', handleWheel as EventListener);
  }, [applyZoomAtPoint, clampPanForScale, zoomFactor]);

  // Listen for onboarding tour actions (tab switching, image toggle)
  useEffect(() => {
    const handler = (e: Event) => {
      const action = (e as CustomEvent).detail?.action;
      if (action === 'switch-to-slide-tab') {
        if (isMobile) {
          setMobilePanel('text');
        } else {
          setSidebarView('slide');
        }
      } else if (action === 'switch-to-global-tab') {
        if (isMobile) {
          setMobilePanel('style');
        } else {
          setSidebarView('global');
        }
      } else if (action === 'enable-image-generate') {
        if (isMobile) {
          setMobilePanel('image');
        } else {
          setSidebarView('slide');
        }
        if (activeSlide && !activeSlide.showImage) {
          handleToggleImageRef.current(true);
        }
        setImageSettingsOpen(true);
      } else if (action === 'switch-to-mobile-slides') {
        if (isMobile) {
          setMobilePanel('slides');
        }
      }
    };
    window.addEventListener('cai:tour-action', handler);
    return () => window.removeEventListener('cai:tour-action', handler);
  }, [activeSlide?.showImage, isMobile]);

  const zoomPercent = Math.round(zoomFactor * 100);
  const canZoomOut = zoomFactor > MIN_CANVAS_ZOOM;
  const canZoomIn = zoomFactor < MAX_CANVAS_ZOOM;

  const handleCanvasZoomIn = () => {
    const viewport = canvasViewportRef.current;
    if (!viewport) return;
    applyZoomAtPoint(
      zoomFactor + ZOOM_STEP_BUTTON,
      viewport.clientWidth / 2,
      viewport.clientHeight / 2
    );
  };

  const handleCanvasZoomOut = () => {
    const viewport = canvasViewportRef.current;
    if (!viewport) return;
    applyZoomAtPoint(
      zoomFactor - ZOOM_STEP_BUTTON,
      viewport.clientWidth / 2,
      viewport.clientHeight / 2
    );
  };

  const handleCanvasZoomReset = () => {
    setZoomFactor(1);
    setPan({ x: 0, y: 0 });
  };

  // ============================================================================
  // MOBILE SWIPE GESTURES - Navigate between slides by swiping on the preview
  // ============================================================================
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    // Ignore if gesture is more vertical than horizontal (scroll intent)
    if (Math.abs(deltaX) < Math.abs(deltaY)) return;
    // Minimum threshold of 50px to count as a swipe
    if (Math.abs(deltaX) < 50) return;

    if (deltaX < 0 && activeIndex < slides.length - 1) {
      // Swipe left -> next slide
      setActiveSlideId(slides[activeIndex + 1].id);
    } else if (deltaX > 0 && activeIndex > 0) {
      // Swipe right -> previous slide
      setActiveSlideId(slides[activeIndex - 1].id);
    }
  };

  return (
    <div className="flex flex-col bg-background text-foreground overflow-hidden" style={{ height: 'var(--app-height)' }}>

      {/* AI Image Edit Modal */}
      {showEditImageModal && activeSlide?.imageUrl && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-card rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl border border-border">
            <h3 className="text-lg font-bold text-foreground mb-4">{t('workspace.editImageTitle')}</h3>

            {/* Current Image Preview */}
            <div className="w-full max-w-[200px] mx-auto mb-4 rounded-lg overflow-hidden border border-border">
              <img
                src={activeSlide.imageUrl}
                alt={t('workspace.currentImageAlt')}
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Edit Prompt */}
            <div className="space-y-3">
              <Label className="text-sm text-muted-foreground font-medium">
                {t('workspace.describeYourEdit')}
              </Label>
              <Textarea
                value={editImagePrompt}
                onChange={(e) => setEditImagePrompt(e.target.value)}
                placeholder={t('workspace.editPlaceholder')}
                className="w-full h-24 resize-none"
                disabled={isEditingImage}
              />
              <Button
                onClick={handleEditImage}
                disabled={isEditingImage || !editImagePrompt.trim()}
                className="w-full"
              >
                {isEditingImage ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    {t('workspace.editing')}
                  </>
                ) : (
                  t('workspace.applyEdit')
                )}
              </Button>
            </div>

            {/* Cancel */}
            <Button
              variant="ghost"
              onClick={handleCloseEditModal}
              disabled={isEditingImage}
              className="w-full mt-4 text-muted-foreground hover:text-foreground"
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Image Upload Modal */}
      {showUploadModal && pendingUploadImage && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-card rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl border border-border">
            <h3 className="text-lg font-bold text-foreground mb-4">{t('workspace.imageOptionsTitle')}</h3>

            {/* Preview */}
            <div className="w-full max-w-[200px] mx-auto mb-4 rounded-lg overflow-hidden border border-border">
              <img
                src={pendingUploadImage.base64}
                alt={t('workspace.imagePreviewAlt')}
                className="w-full h-auto object-contain"
              />
            </div>
            {/* Detected ratio info */}
            <p className="text-center text-muted-foreground text-xs mb-4">
              {t('workspace.detectedInfo', {
                width: pendingUploadImage.width,
                height: pendingUploadImage.height,
                ratio: pendingUploadImage.detectedRatio,
              })}
            </p>

            {/* Options */}
            <div className="space-y-4">
              {/* Use As-Is Button */}
              <Button
                variant="secondary"
                onClick={handleUseImageAsIs}
                disabled={isStylizing}
                className="w-full"
              >
                {t('workspace.useAsIs')}
              </Button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border"></div>
                <span className="text-muted-foreground text-sm">{t('workspace.orSeparator')}</span>
                <div className="flex-1 h-px bg-border"></div>
              </div>

              {/* Stylize Section */}
              <div className="space-y-3">
                <Label className="text-sm text-muted-foreground font-medium">
                  {t('workspace.stylizeWithAI')}
                </Label>
                <Textarea
                  value={stylizePrompt}
                  onChange={(e) => setStylizePrompt(e.target.value)}
                  placeholder={t('workspace.stylizePlaceholder')}
                  className="w-full h-20 resize-none"
                  disabled={isStylizing}
                />
                <Button
                  onClick={handleStylizeImage}
                  disabled={isStylizing || !stylizePrompt.trim()}
                  className="w-full"
                >
                  {isStylizing ? (
                    <>
                      <Loader2 className="animate-spin h-5 w-5 mr-2" />
                      {t('workspace.stylizing')}
                    </>
                  ) : (
                    t('workspace.stylizeWithAI')
                  )}
                </Button>
              </div>
            </div>

            {/* Cancel */}
            <Button
              variant="ghost"
              onClick={handleCancelUpload}
              disabled={isStylizing}
              className="w-full mt-4 text-muted-foreground hover:text-foreground"
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Top Header */}
      <div className="h-12 md:h-14 px-2 md:px-4 border-b border-border bg-card flex items-center justify-between flex-shrink-0">
        <div className="flex items-center min-w-0">
          {/* Back button: icon-only on mobile, full text on desktop */}
          <Button variant="ghost" size="icon" onClick={onBack} className="md:hidden h-8 w-8 text-muted-foreground hover:text-foreground flex-shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onBack} className="hidden md:flex text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {t('workspace.backToDashboard')}
          </Button>
          <div className="w-px h-6 bg-border mx-2 md:mx-3 hidden md:block" />
          {/* Mobile: project name + slide indicator stacked */}
          <div className="flex flex-col md:flex-row md:items-center min-w-0">
            <h1 className="font-semibold text-sm md:text-base truncate max-w-[140px] md:max-w-[340px]">{projectName || 'Untitled'}</h1>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground md:hidden">
              {t('mobile.editingSlide', { current: activeIndex + 1, total: slides.length })}
            </span>
          </div>
          <span className="ml-2 md:ml-3 text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full bg-secondary text-muted-foreground border border-border whitespace-nowrap hidden md:inline">
            {getSaveBadgeLabel()}
          </span>
        </div>

        <div className="flex items-center gap-1 md:gap-2">
          {/* Restart Tour: hidden on mobile */}
          <Button
            variant="ghost"
            size="icon"
            onClick={restartTour}
            title={t('onboarding.controls.restartTour')}
            className="h-8 w-8 hidden md:flex"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          {/* Theme toggle: hidden on mobile */}
          {onEditorThemeToggle && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onEditorThemeToggle}
              title={editorTheme === 'light' ? t('create.darkMode') : t('create.lightMode')}
              className="h-8 w-8 hidden md:flex"
            >
              {editorTheme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>
          )}
          <div className="relative">
            <Button
              ref={exportButtonRef}
              data-tour="editor-export"
              onClick={() => {
                setExportMode('all');
                setSelectedExportSlideIds(new Set(activeSlideId ? [activeSlideId] : []));
                setShowExportPopover((prev) => !prev);
              }}
              className="px-3 md:px-4"
              size={isMobile ? 'sm' : 'default'}
              aria-haspopup="dialog"
              aria-expanded={showExportPopover}
            >
              <Download className="h-4 w-4 mr-1.5 md:mr-2" />
              <span>{t('workspace.export')}</span>
            </Button>

            {showExportPopover && (
              <div
                ref={exportPopoverRef}
                role="dialog"
                aria-label={t('workspace.exportTitle')}
                className="absolute right-0 top-full mt-2 w-[calc(100vw-1rem)] md:w-[420px] max-w-[calc(100vw-1rem)] rounded-xl border border-border bg-card shadow-xl z-50"
              >
                <div className="p-4 border-b border-border">
                  <div className="flex items-start gap-2">
                    <Download className="h-4 w-4 mt-1 text-primary" />
                    <div>
                      <h3 className="text-base font-semibold text-foreground">{t('workspace.exportTitle')}</h3>
                      <p className="text-sm text-muted-foreground">{t('workspace.exportSubtitle')}</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                    {t('workspace.exportSelectionLabel')}
                  </p>

                  <button
                    type="button"
                    onClick={() => setExportMode('all')}
                    className={cn(
                      "w-full rounded-lg border p-3 text-left transition-colors",
                      exportMode === 'all' ? "border-primary/60 bg-primary/10" : "border-border hover:border-primary/30"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "mt-1 h-4 w-4 rounded-full border-2",
                        exportMode === 'all' ? "border-primary bg-primary" : "border-muted-foreground/40"
                      )} />
                      <div>
                        <p className="text-sm font-medium text-foreground">{t('workspace.exportAll')}</p>
                        <p className="text-xs text-muted-foreground">{t('workspace.slidesAvailable', { count: slides.length })}</p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setExportMode('selected')}
                    className={cn(
                      "w-full rounded-lg border p-3 text-left transition-colors",
                      exportMode === 'selected' ? "border-primary/60 bg-primary/10" : "border-border hover:border-primary/30"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "mt-1 h-4 w-4 rounded-full border-2",
                        exportMode === 'selected' ? "border-primary bg-primary" : "border-muted-foreground/40"
                      )} />
                      <div>
                        <p className="text-sm font-medium text-foreground">{t('workspace.exportSpecificSlides')}</p>
                        <p className="text-xs text-muted-foreground">{t('workspace.slidesAvailable', { count: slides.length })}</p>
                      </div>
                    </div>
                  </button>

                  {exportMode === 'selected' && (
                    <div className="rounded-lg border border-border overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-secondary/40">
                        <span className="text-xs text-muted-foreground">
                          {t('workspace.slidesAvailable', { count: slides.length })}
                        </span>
                        <button
                          type="button"
                          onClick={isAllExportSlidesSelected ? handleClearExportSlides : handleSelectAllExportSlides}
                          className="text-xs font-medium text-primary hover:underline"
                        >
                          {isAllExportSlidesSelected ? t('workspace.clearSelection') : t('workspace.selectAllSlides')}
                        </button>
                      </div>

                      <div className="max-h-64 overflow-y-auto p-2 space-y-2">
                        {slides.map((slide, index) => {
                          const isSelected = selectedExportSlideIds.has(slide.id);
                          return (
                            <div
                              key={slide.id}
                              onClick={() => toggleExportSlideSelection(slide.id)}
                              className={cn(
                                "w-full rounded-md border p-2 text-left transition-colors cursor-pointer",
                                isSelected ? "border-primary/50 bg-primary/10" : "border-border hover:border-primary/30"
                              )}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  toggleExportSlideSelection(slide.id);
                                }
                              }}
                            >
                              <div className="flex items-start gap-2">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleExportSlideSelection(slide.id)}
                                  onClick={(event) => event.stopPropagation()}
                                  className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                                />
                                <div className={cn(
                                  "h-6 w-6 aspect-square flex-shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold",
                                  isSelected ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                                )}>
                                  {index + 1}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm text-foreground truncate">{t('workspace.slideLabel', { number: index + 1 })}</p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {getExportSlidePreviewText(slide.content)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {exportMode === 'selected' && !hasSelectedExportSlides && (
                    <p className="text-xs text-destructive">{t('workspace.noSlidesSelected')}</p>
                  )}
                </div>

                <div className="p-4 border-t border-border flex items-center justify-end gap-2">
                  <Button variant="secondary" onClick={() => setShowExportPopover(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleConfirmExport}
                    disabled={isDownloading || (exportMode === 'selected' && !hasSelectedExportSlides)}
                  >
                    {exportMode === 'selected' ? t('workspace.exportSelected') : t('workspace.confirmExport')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden file input for project import */}
      <input
        type="file"
        ref={projectImportRef}
        className="hidden"
        accept=".json"
        onChange={handleImportProject}
      />

      {/* ================================================================ */}
      {/* DESKTOP LAYOUT: Traditional 3-panel side-by-side               */}
      {/* ================================================================ */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        {/* Left Sidebar: Slide List */}
        <div data-tour="editor-slide-list" className="w-64 border-r border-border flex flex-col bg-card flex-shrink-0 z-20">
          <div className="p-4 border-b border-border">
            <div className="flex justify-between items-center">
              <h2 className="font-bold text-sm uppercase tracking-wide text-muted-foreground">
                {t('workspace.slidesCount', { count: slides.length })}
              </h2>
              <div className="flex items-center gap-1">
                <Button
                  data-tour="editor-batch-mode"
                  variant={batchMode ? "default" : "ghost"}
                  size="icon"
                  onClick={() => {
                    setBatchMode(!batchMode);
                    if (batchMode) {
                      setSelectedSlideIds(new Set());
                      setSlideGenerationStatus({});
                    }
                  }}
                  disabled={batchGenerating}
                  title={batchMode ? t('workspace.exitBatchMode') : t('workspace.batchGenerate')}
                  className="h-8 w-8"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {batchMode && (
              <div className="p-2 bg-secondary rounded-lg mb-2 space-y-2">
                <div className="flex items-center justify-between">
                  <Button variant="link" size="sm" onClick={toggleSelectAll} className="text-xs text-primary p-0 h-auto">
                    {selectedSlideIds.size === slides.length ? t('workspace.deselectAll') : t('workspace.selectAll')}
                  </Button>
                  <span className="text-xs text-muted-foreground">{selectedSlideIds.size} selected</span>
                </div>
                <Button onClick={handleBatchGenerateImages} disabled={selectedSlideIds.size === 0 || batchGenerating} className="w-full text-xs" size="sm">
                  {batchGenerating ? <span aria-live="polite" aria-atomic="true">{batchGeneratingLabel}</span> : t('workspace.generateImages', { count: selectedSlideIds.size })}
                </Button>
              </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={slides.map(s => s.id)} strategy={verticalListSortingStrategy}>
                {slides.map((slide, idx) => (
                  <SortableSlideItem
                    key={slide.id}
                    slide={slide}
                    index={idx}
                    isActive={activeSlideId === slide.id}
                    batchMode={batchMode}
                    isSelected={selectedSlideIds.has(slide.id)}
                    generationStatus={slideGenerationStatus[slide.id]}
                    isGenerating={generatingSlideIds.has(slide.id)}
                    onSelect={() => setActiveSlideId(slide.id)}
                    onToggleSelection={() => toggleSlideSelection(slide.id)}
                    onDelete={() => handleDeleteSlide(slide.id)}
                    onDuplicate={() => handleDuplicateSlide(slide.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
          <div className="p-4 border-t border-border">
            <Button data-tour="editor-add-slide" variant="outline" onClick={handleAddSlide} className="w-full border-dashed text-muted-foreground hover:text-foreground">
              <Plus className="h-4 w-4 mr-2" />
              {t('workspace.addSlideFooter')}
            </Button>
          </div>
        </div>

        {/* Center: Canvas Preview (desktop) */}
        <div className="flex-1 flex flex-col relative bg-background min-w-0">
          <div className="flex-1 flex overflow-hidden">
            <div className={cn("flex-1 flex items-center justify-center overflow-hidden relative", editorTheme === 'light' ? "bg-slate-100" : "bg-black")}>
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
                <div data-tour="editor-zoom" className="flex items-center gap-1 rounded-full bg-card/95 border border-border shadow-lg px-2 py-1.5 backdrop-blur-sm">
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={handleCanvasZoomOut} disabled={!canZoomOut} aria-label={t('workspace.canvasZoomOut')} title={t('workspace.canvasZoomOut')}>
                    <span className="text-base leading-none">-</span>
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="h-7 min-w-[66px] rounded-full px-3 text-xs font-semibold text-muted-foreground hover:text-foreground" onClick={handleCanvasZoomReset} aria-label={t('workspace.canvasZoomReset')} title={t('workspace.canvasZoomReset')}>
                    {t('workspace.canvasZoomValue', { value: zoomPercent })}
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={handleCanvasZoomIn} disabled={!canZoomIn} aria-label={t('workspace.canvasZoomIn')} title={t('workspace.canvasZoomIn')}>
                    <span className="text-base leading-none">+</span>
                  </Button>
                </div>
              </div>
              <div ref={canvasViewportRef} className="w-full h-full overflow-hidden flex items-center justify-center">
                <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${fitScale * zoomFactor})`, transformOrigin: 'center center', transition: 'transform 0.2s ease-out' }} className="flex-shrink-0 shadow-2xl">
                  <div id="preview-slide-capture" data-tour="editor-preview" data-slide-id={activeSlide.id} className="relative" style={{ width: `${PREVIEW_WIDTH}px`, height: `${previewHeight}px`, backgroundColor: (activeSlide.theme || theme) === 'DARK' ? '#000000' : '#FFFFFF' }}>
                    {renderSlide(activeSlide, activeIndex, false)}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Properties (desktop) */}
            <div data-tour="editor-right-panel" className="w-80 xl:w-96 bg-card border-l border-border p-4 flex flex-col overflow-y-auto flex-shrink-0 z-20">
              {/* Sidebar View Toggle */}
              <div className="mb-4 flex items-center gap-2">
                <div data-tour="editor-sidebar-toggle" className="flex-1 flex bg-secondary rounded-lg p-1">
                  <Button variant="ghost" size="sm" onClick={() => setSidebarView('slide')} className={cn("flex-1 px-3 py-1.5 text-xs font-semibold", sidebarView === 'slide' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground')}>
                    <Type className="h-3 w-3 mr-1.5" />
                    {t('workspace.slidePanel')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setSidebarView('global')} className={cn("flex-1 px-3 py-1.5 text-xs font-semibold", sidebarView === 'global' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground')}>
                    <Settings className="h-3 w-3 mr-1.5" />
                    {t('workspace.globalPanel')}
                  </Button>
                </div>
              </div>

              {/* Global Settings Section (desktop) */}
              {sidebarView === 'global' && (
                <div className="mb-6 border-b border-border pb-6">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">{t('workspace.globalSettings')}</h3>
                  {onStyleChange && (
                    <div className="mb-4 p-3 bg-secondary rounded-lg">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">{t('workspace.carouselStyle')}</Label>
                        <Select value={style} onValueChange={(value) => handleConvertStyle(value as CarouselStyle)}>
                          <SelectTrigger className="w-[140px] h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {styleOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  <div data-tour="editor-theme-toggle" className="flex items-center justify-between mb-4">
                    <Label className="text-sm font-medium">{t('workspace.theme')}</Label>
                    <div className="flex bg-secondary rounded-lg p-1">
                      <Button variant="ghost" size="sm" onClick={() => setTheme('LIGHT')} className={cn("px-3 py-1 text-xs font-bold", theme === 'LIGHT' ? 'bg-white text-black shadow' : 'text-muted-foreground hover:text-foreground')}>
                        <Sun className="h-3 w-3 mr-1" /> {t('workspace.light')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setTheme('DARK')} className={cn("px-3 py-1 text-xs font-bold", theme === 'DARK' ? 'bg-black text-white shadow' : 'text-muted-foreground hover:text-foreground')}>
                        <Moon className="h-3 w-3 mr-1" /> {t('workspace.dark')}
                      </Button>
                    </div>
                  </div>
                  <div data-tour="editor-accent-color" className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">{t('workspace.useAccentColor')}</Label>
                    <Switch checked={showAccent} onCheckedChange={setShowAccent} />
                  </div>
                  {showAccent && (
                    <div className="flex items-center gap-3 mb-4 bg-secondary p-2 rounded-lg border border-border">
                      <input type="color" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0 p-0 bg-transparent" title={t('workspace.pickColor')} />
                      <Input type="text" value={accentColor} onChange={(e) => setAccentColor(e.target.value)} className="flex-1 font-mono uppercase" maxLength={7} placeholder="#RRGGBB" />
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">{t('workspace.slideNumbers')}</Label>
                    <Switch checked={showSlideNumbers} onCheckedChange={setShowSlideNumbers} />
                  </div>
                  {showSlideNumbers && (
                    <div className="flex items-center gap-2 mb-4 bg-secondary p-1 rounded-lg">
                      {slideNumberPositionOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant="ghost"
                          size="sm"
                          onClick={() => setSlideNumberPosition(option.value)}
                          className={cn("flex-1 px-2 py-1 text-xs font-bold", slideNumberPosition === option.value ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground')}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-sm font-medium">{t('workspace.verifiedBadge')}</Label>
                    <Switch checked={showVerifiedBadge} onCheckedChange={setShowVerifiedBadge} />
                  </div>
                  <div data-tour="editor-font-select" className="mb-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">{t('workspace.fontStyle')}</Label>
                      <Select value={fontStyle} onValueChange={(value) => setFontStyle(value as FontStyle)}>
                        <SelectTrigger className="w-[160px] h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {fontOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mb-4">
                    <Button variant="ghost" size="sm" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground p-2">
                      <span className="font-semibold">{t('workspace.advancedOptions')}</span>
                      {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                    {showAdvancedOptions && (
                      <div className="mt-2 space-y-3">
                        <div className="p-3 bg-secondary rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-medium">{t('workspace.imageGenerationStyle')}</Label>
                            <Button variant="link" size="sm" onClick={() => setGlobalImageStyle(DEFAULT_IMAGE_STYLE)} className="text-xs text-muted-foreground hover:text-foreground p-0 h-auto" title={t('workspace.resetToDefault')}>{t('workspace.reset')}</Button>
                          </div>
                          <Textarea value={globalImageStyle} onChange={(e) => setGlobalImageStyle(e.target.value)} placeholder={t('workspace.imageStylePlaceholder')} className="w-full h-16 text-xs resize-none" />
                          <p className="text-[10px] text-muted-foreground mt-1">{t('workspace.imageStyleAppliedHint')}</p>
                        </div>
                        <div className="p-3 bg-secondary rounded-lg">
                          <Label className="text-sm font-medium mb-2 block">{t('workspace.refineAllWithAI')}</Label>
                          <Textarea value={globalFeedback} onChange={(e) => setGlobalFeedback(e.target.value)} placeholder={t('workspace.refineAllPlaceholder')} className="w-full h-16 resize-none mb-2" disabled={isRefining} />
                          <Button onClick={handleGlobalRefine} disabled={!globalFeedback.trim() || isRefining} className="w-full bg-purple-600 hover:bg-purple-500" size="sm">
                            {isRefining ? (<><Loader2 className="animate-spin h-4 w-4 mr-2" />{t('workspace.refining')}</>) : (<><Sparkles className="h-4 w-4 mr-2" />{t('workspace.refineAllSlides')}</>)}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Per-Slide Settings Section (desktop) */}
              {sidebarView === 'slide' && (
                <>
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">{t('workspace.editSlide')}</h3>
                  <div data-tour="editor-text-editor" className="flex-shrink-0">
                    <RichTextEditor
                      content={activeSlide.content}
                      onChange={handleTextChange}
                      placeholder={t('editor.richText.placeholder')}
                      fontFamily={activeSlide.fontStyle || 'GLOBAL'}
                      onFontFamilyChange={(value) => {
                        const newSlides = [...slides];
                        newSlides[activeIndex] = { ...activeSlide, fontStyle: value === 'GLOBAL' ? undefined : value as FontStyle };
                        onUpdateSlides(newSlides);
                      }}
                      fontScale={activeSlide.fontScale ?? 1.0}
                      onFontScaleChange={(scale) => {
                        const newSlides = [...slides];
                        newSlides[activeIndex] = { ...activeSlide, fontScale: scale === 1.0 ? undefined : scale };
                        onUpdateSlides(newSlides);
                      }}
                    />
                  </div>
                  <div data-tour="editor-image-section" className="mb-4 border border-border rounded-lg">
                    <div className="flex items-center justify-between p-3 bg-secondary/50 cursor-pointer hover:bg-secondary/80 transition-colors rounded-t-lg" onClick={() => activeSlide.showImage && setImageSettingsOpen(!imageSettingsOpen)}>
                      <div className="flex items-center gap-2">
                        {activeSlide.showImage && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setImageSettingsOpen(!imageSettingsOpen); }} className="text-muted-foreground hover:text-foreground">
                            {imageSettingsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        )}
                        <span className="font-semibold text-sm">{t('workspace.illustrationImage')}</span>
                      </div>
                      <Switch checked={activeSlide.showImage} onCheckedChange={(checked) => { handleToggleImage(checked); if (checked) setImageSettingsOpen(true); }} />
                    </div>
                    {activeSlide.showImage && imageSettingsOpen && (
                      <div className="p-3 border-t border-border animate-fade-in">
                        {style === CarouselStyle.STORYTELLER && (
                          <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
                            <span className="text-xs text-muted-foreground">{t('workspace.topFadeOverlay')}</span>
                            <Switch checked={activeSlide.overlayImage !== false} onCheckedChange={(checked) => handleToggleOverlay(checked)} />
                          </div>
                        )}
                        {style === CarouselStyle.TWITTER && (
                          <div className="mb-4 pb-4 border-b border-border">
                            <span className="text-xs text-muted-foreground block mb-2">{t('workspace.imagePosition')}</span>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, contentLayout: 'default' }; onUpdateSlides(newSlides); }} className={cn("flex-1 px-2 py-1.5 text-[10px] font-medium", (!activeSlide.contentLayout || activeSlide.contentLayout === 'default') ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground')}>{t('workspace.positionBelow')}</Button>
                              <Button variant="ghost" size="sm" onClick={() => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, contentLayout: 'image-after-title' }; onUpdateSlides(newSlides); }} className={cn("flex-1 px-2 py-1.5 text-[10px] font-medium", activeSlide.contentLayout === 'image-after-title' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground')}>{t('workspace.positionAfterTitle')}</Button>
                              <Button variant="ghost" size="sm" onClick={() => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, contentLayout: 'image-first' }; onUpdateSlides(newSlides); }} className={cn("flex-1 px-2 py-1.5 text-[10px] font-medium", activeSlide.contentLayout === 'image-first' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground')}>{t('workspace.positionTop')}</Button>
                            </div>
                            {activeSlide.contentLayout === 'image-after-title' && (
                              <div className="mt-3 pt-3 border-t border-border">
                                <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                                  <span>{t('workspace.imageTextSpacing')}</span>
                                  <span>{activeSlide.imageTextSpacing ?? 16}px</span>
                                </div>
                                <Slider value={[activeSlide.imageTextSpacing ?? 16]} min={0} max={64} step={4} onValueChange={(value) => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imageTextSpacing: value[0] }; onUpdateSlides(newSlides); }} />
                              </div>
                            )}
                          </div>
                        )}
                        {activeSlide.imageUrl ? (
                          <div className="relative aspect-square rounded-md overflow-hidden mb-3 border border-border">
                            <img src={activeSlide.imageUrl} alt={t('workspace.thumbnailAlt')} className="w-full h-full object-cover" />
                            <div className="absolute top-1 right-1 flex gap-1">
                              <Button variant="ghost" size="icon" onClick={handleOpenEditModal} className="bg-black/50 hover:bg-primary text-white h-6 w-6" title={t('workspace.editWithAI')}><Pencil className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imageUrl: undefined }; onUpdateSlides(newSlides); }} className="bg-black/50 hover:bg-destructive text-white h-6 w-6" title={t('workspace.removeImage')}><X className="h-4 w-4" /></Button>
                            </div>
                          </div>
                        ) : (
                          <div className="h-32 bg-secondary rounded-md border-2 border-dashed border-border flex items-center justify-center mb-3">
                            <span className="text-muted-foreground text-xs">{t('workspace.noImageSelected')}</span>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2 mb-4">
                          <Button variant="secondary" onClick={() => fileInputRef.current?.click()} className="text-xs"><Upload className="h-4 w-4 mr-2" />{t('workspace.uploadFile')}</Button>
                          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />
                          <Button data-tour="editor-image-generate" onClick={handleGenerateImage} disabled={generatingSlideIds.has(activeSlide.id)} className="text-xs">
                            {generatingSlideIds.has(activeSlide.id) ? (<><Loader2 className="animate-spin h-4 w-4 mr-2" />{t('workspace.generating')}</>) : (<><Sparkles className="h-4 w-4 mr-2" />{t('workspace.aiGenerate')}</>)}
                          </Button>
                        </div>
                        {activeSlide.imageUrl && (
                          <div className="space-y-4 pt-2 border-t border-border">
                            {(style === CarouselStyle.STORYTELLER || !activeSlide.overlayImage) && (
                              <div>
                                <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.imageHeight')}</span><span>{activeSlide.imageScale || 50}%</span></div>
                                <Slider value={[activeSlide.imageScale || 50]} min={10} max={90} step={1} onValueChange={(value) => handleImageScaleChange(value[0])} />
                              </div>
                            )}
                            <div>
                              <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.verticalPosition')}</span><span>{activeSlide.imageOffsetY !== undefined ? activeSlide.imageOffsetY : 50}%</span></div>
                              <Slider value={[activeSlide.imageOffsetY !== undefined ? activeSlide.imageOffsetY : 50]} min={0} max={100} step={1} onValueChange={(value) => handleImageOffsetChange(value[0])} />
                              <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>{t('workspace.positionScaleTop')}</span><span>{t('workspace.positionScaleCenter')}</span><span>{t('workspace.positionScaleBottom')}</span></div>
                            </div>
                            {(style === CarouselStyle.STORYTELLER && activeSlide.overlayImage !== false) && (
                              <div>
                                <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.overlayFadeHeight')}</span><span>{activeSlide.gradientHeight !== undefined ? activeSlide.gradientHeight : 60}%</span></div>
                                <Slider value={[activeSlide.gradientHeight !== undefined ? activeSlide.gradientHeight : 60]} min={0} max={100} step={1} onValueChange={(value) => handleGradientHeightChange(value[0])} />
                              </div>
                            )}
                            {style === CarouselStyle.TWITTER && (
                              <>
                                <div>
                                  <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.imageZoom')}</span><span>{Math.round((activeSlide.imageZoom ?? layoutSettings.imageZoom) * 100)}%</span></div>
                                  <Slider value={[activeSlide.imageZoom ?? layoutSettings.imageZoom]} min={0.5} max={2.0} step={0.1} onValueChange={(value) => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imageZoom: value[0] }; onUpdateSlides(newSlides); }} />
                                  <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>{t('workspace.zoomOut')}</span><span>100%</span><span>{t('workspace.zoomIn')}</span></div>
                                </div>
                                <div>
                                  <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{t('workspace.imageMargin')}</span><span>{activeSlide.imageMargin ?? layoutSettings.imageMargin}px</span></div>
                                  <Slider value={[activeSlide.imageMargin ?? layoutSettings.imageMargin]} min={0} max={32} step={4} onValueChange={(value) => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imageMargin: value[0] }; onUpdateSlides(newSlides); }} />
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        <div className="mt-3">
                          <Label className="text-xs text-muted-foreground block mb-1">{t('workspace.promptOverride')}</Label>
                          <Textarea className="w-full h-16 text-xs resize-none" placeholder={t('workspace.describeImagePlaceholder')} value={activeSlide.imagePrompt || ''} onChange={(e) => { const newSlides = [...slides]; newSlides[activeIndex] = { ...activeSlide, imagePrompt: e.target.value }; onUpdateSlides(newSlides); }} />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* MOBILE LAYOUT: Preview + Bottom Sheet + Toolbar                */}
      {/* ================================================================ */}
      {isMobile && (
        <>
          {/* Mobile Preview - shrinks when panel is open, expands when closed */}
          <div
            className={cn(
              "flex-1 flex items-center justify-center overflow-hidden relative min-h-0",
              editorTheme === 'light' ? "bg-slate-100" : "bg-black"
            )}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              ref={canvasViewportRef}
              className="w-full h-full overflow-hidden flex items-center justify-center"
            >
              <div
                style={{
                  transform: `scale(${fitScale * zoomFactor})`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.2s ease-out'
                }}
                className="flex-shrink-0 shadow-2xl"
              >
                <div
                  id="preview-slide-capture-mobile"
                  data-tour="editor-preview-mobile"
                  data-slide-id={activeSlide.id}
                  className="relative"
                  style={{ width: `${PREVIEW_WIDTH}px`, height: `${previewHeight}px`, backgroundColor: (activeSlide.theme || theme) === 'DARK' ? '#000000' : '#FFFFFF' }}
                >
                  {renderSlide(activeSlide, activeIndex, false)}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Bottom Sheet Editor */}
          <MobileEditor
            activePanel={mobilePanel}
            onPanelChange={setMobilePanel}
            slides={slides}
            activeSlideId={activeSlideId}
            activeSlide={activeSlide}
            activeIndex={activeIndex}
            batchMode={batchMode}
            selectedSlideIds={selectedSlideIds}
            generatingSlideIds={generatingSlideIds}
            slideGenerationStatus={slideGenerationStatus}
            batchGenerating={batchGenerating}
            batchGeneratingLabel={batchGeneratingLabel}
            sensors={sensors}
            onDragEnd={handleDragEnd}
            onSlideSelect={(slideId) => {
              setActiveSlideId(slideId);
              setMobilePanel('text');
            }}
            onToggleSelection={toggleSlideSelection}
            onDeleteSlide={handleDeleteSlide}
            onDuplicateSlide={handleDuplicateSlide}
            onAddSlide={handleAddSlide}
            onToggleBatchMode={() => {
              setBatchMode(!batchMode);
              if (batchMode) { setSelectedSlideIds(new Set()); setSlideGenerationStatus({}); }
            }}
            onToggleSelectAll={toggleSelectAll}
            onBatchGenerateImages={handleBatchGenerateImages}
            SortableSlideItem={SortableSlideItem}
            onUpdateSlides={onUpdateSlides}
            onTextChange={handleTextChange}
            style={style}
            layoutSettings={layoutSettings}
            imageSettingsOpen={imageSettingsOpen}
            setImageSettingsOpen={setImageSettingsOpen}
            onToggleImage={handleToggleImage}
            onToggleOverlay={handleToggleOverlay}
            onGenerateImage={handleGenerateImage}
            onFileUpload={handleFileUpload}
            onOpenEditModal={handleOpenEditModal}
            onImageScaleChange={handleImageScaleChange}
            onImageOffsetChange={handleImageOffsetChange}
            onGradientHeightChange={handleGradientHeightChange}
            fileInputRef={fileInputRef}
            theme={theme}
            setTheme={setTheme}
            onConvertStyle={handleConvertStyle}
            onStyleChange={onStyleChange}
            fontStyle={fontStyle}
            setFontStyle={setFontStyle}
            showAccent={showAccent}
            setShowAccent={setShowAccent}
            accentColor={accentColor}
            setAccentColor={setAccentColor}
            styleOptions={styleOptions}
            fontOptions={fontOptions}
            showSlideNumbers={showSlideNumbers}
            setShowSlideNumbers={setShowSlideNumbers}
            showVerifiedBadge={showVerifiedBadge}
            setShowVerifiedBadge={setShowVerifiedBadge}
            slideNumberPosition={slideNumberPosition}
            setSlideNumberPosition={setSlideNumberPosition}
            slideNumberPositionOptions={slideNumberPositionOptions}
            globalImageStyle={globalImageStyle}
            setGlobalImageStyle={setGlobalImageStyle}
            defaultImageStyle={DEFAULT_IMAGE_STYLE}
            globalFeedback={globalFeedback}
            setGlobalFeedback={setGlobalFeedback}
            isRefining={isRefining}
            onGlobalRefine={handleGlobalRefine}
          />

          {/* Mobile Bottom Toolbar */}
          <MobileToolbar
            activePanel={mobilePanel}
            onPanelChange={setMobilePanel}
          />
        </>
      )}

      {/* Hidden off-screen container for thumbnail generation of first slide */}
      {onThumbnailGenerated && slides.length > 0 && (
        <div
          id="thumbnail-capture"
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: '-2000px',
            top: 0,
            width: `${PREVIEW_WIDTH}px`,
            height: `${previewHeight}px`,
            overflow: 'hidden',
            pointerEvents: 'none',
            opacity: 1,
            visibility: 'visible',
          }}
        >
          {renderSlide(slides[0], 0, true)}
        </div>
      )}
    </div>
  );
};

export default Workspace;
